import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  Upload, 
  FileText, 
  CheckCircle2, 
  AlertTriangle, 
  HelpCircle, 
  Eye, 
  ChevronDown, 
  Check, 
  X, 
  AlertCircle, 
  BookOpen, 
  Headphones, 
  PenTool, 
  Download, 
  Trash2, 
  Volume2, 
  Music,
  ArrowLeft,
  Copy,
  Image,
  Plus,
  Loader2,
  ChevronRight,
  ChevronLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { questionService } from "@/services/questionService";
import { questionMediaUploadService, isPermanentMediaUrl } from "@/services/questionMediaUploadService";
import { mediaService } from "@/services/mediaService";
import { storageService } from "@/services/storageService";
import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeReadingSection, getReadingLabelByKey, getAutomaticDifficulty } from "@/data/tefStructure";
import { CE_IMPORT_TEMPLATES, CO_IMPORT_TEMPLATES, EE_IMPORT_TEMPLATES } from "@/data/importTemplates";
import type { ImportTemplate } from "@/data/importTemplates";
import { validateQuestion, normalizeQuestion } from "@/utils/questionValidation";
import { normalizeImportedQuestionsPayload, normalizeQuestionOptions, normalizeCompetency, getQuestionAudioUrl, getOptionImageUrl } from "@/utils/questionNormalizer";
import { ClipboardMediaPasteBox } from "@/components/import/ClipboardMediaPasteBox";
import { AudioPlayerWithSpeed } from "@/components/audio/AudioPlayerWithSpeed";
import { isBareMediaName, resolveBareMediaNameToPublicUrl } from "@/utils/mediaUrlResolver";
import { buildQuestionFingerprintHash, getStableQuestionForFingerprint } from "@/utils/questionFingerprint";
import { saveImportDraft, getImportDraft, clearImportDraft, type LocalFileMetadata } from "@/services/importDraftService";

// Official category lists
const READING_SECTIONS = [
  "Documents divers",
  "Phrases lacunaires / Textes lacunaires",
  "Lecture rapide de textes et de graphiques",
  "Documents administratifs et professionnels",
  "Articles de presse"
];

const LISTENING_SECTIONS = [
  "Dialogues associés à des images",
  "Annonces publiques",
  "Messages courts / situations téléphoniques",
  "Micro-trottoirs",
  "Chroniques radio",
  "Interviews",
  "Reportages",
  "Documents divers"
];

const WRITING_SECTIONS = [
  "Section A",
  "Section B"
];


// Helper parsers from legacy format
function parseBlock(blockText: string) {
  const fields: Record<string, string> = {};
  let normalizedBlock = blockText.replace(/\s+(TITLE|COMPETENCY|SECTION|TYPE|DIFFICULTY|TAGS|INSTRUCTION|QUESTION|PROMPT|BODY|OPTIONS|CORRECT_ANSWER|ANSWER|EXPLANATION|STATUS|IMAGE_URL|AUDIO_URL|AUDIO_FILE|AUDIO_TYPE|TYPE_AUDIO|SOUS_PARTIE|SUPPORT_IMAGE|IMAGE|OPTION_A_IMAGE|OPTION_B_IMAGE|OPTION_C_IMAGE|OPTION_D_IMAGE|AUDIO):/gi, "\n$1:");
  normalizedBlock = normalizedBlock.replace(/(CORRECT_ANSWER|ANSWER):\s*([A-D])\s*(EXPLANATION|STATUS|TITLE|COMPETENCY|SECTION|TYPE|DIFFICULTY|TAGS|INSTRUCTION|QUESTION|PROMPT|BODY|OPTIONS):/gi, "$1: $2\n$3:");
  normalizedBlock = normalizedBlock.replace(/(STATUS:\s*\w+)\s*(===?\s*END\s*QUESTION\s*===?)/gi, "$1\n$2");

  const markerPattern = /(TITLE:|COMPETENCY:|SECTION:|TYPE:|DIFFICULTY:|TAGS:|INSTRUCTION:|QUESTION:|PROMPT:|BODY:|OPTIONS:|CORRECT_ANSWER:|ANSWER:|EXPLANATION:|STATUS:|IMAGE_URL:|AUDIO_URL:|AUDIO_FILE:|AUDIO_TYPE:|TYPE_AUDIO:|SOUS_PARTIE:|SUPPORT_IMAGE:|IMAGE:|OPTION_A_IMAGE:|OPTION_B_IMAGE:|OPTION_C_IMAGE:|OPTION_D_IMAGE:|AUDIO:)/gi;
  let match;
  const matches: { marker: string; index: number; length: number }[] = [];
  
  while ((match = markerPattern.exec(normalizedBlock)) !== null) {
    matches.push({
      marker: match[1].toUpperCase().replace(":", ""),
      index: match.index,
      length: match[1].length
    });
  }
  
  if (matches.length === 0) {
    return fields;
  }
  
  for (let i = 0; i < matches.length; i++) {
    const current = matches[i];
    const startIdx = current.index + current.length;
    const endIdx = (i + 1 < matches.length) ? matches[i + 1].index : normalizedBlock.length;
    fields[current.marker] = normalizedBlock.substring(startIdx, endIdx).trim();
  }
  
  return fields;
}

function extractOptionsFromFreeText(blockText: string): { A: string; B: string; C: string; D: string } | null {
  const result = { A: "", B: "", C: "", D: "" };
  const optionRegex = /(?:^|[\s\n])([A-D])[)\.:)]\s+/gm;
  const matches: { label: "A" | "B" | "C" | "D"; index: number; length: number }[] = [];
  let m;
  while ((m = optionRegex.exec(blockText)) !== null) {
    const label = m[1].toUpperCase() as "A" | "B" | "C" | "D";
    matches.push({ label, index: m.index, length: m[0].length });
  }

  if (matches.length < 2) return null;
  matches.sort((a, b) => a.index - b.index);

  const terminatorRegex = /\b(CORRECT_ANSWER|ANSWER|EXPLANATION|STATUS|===)\b/i;
  const terminatorMatch = terminatorRegex.exec(blockText);
  const terminatorIdx = terminatorMatch ? terminatorMatch.index : blockText.length;

  for (let i = 0; i < matches.length; i++) {
    const startIdx = matches[i].index + matches[i].length;
    const endIdx = i + 1 < matches.length ? matches[i + 1].index : terminatorIdx;
    result[matches[i].label] = blockText.substring(startIdx, Math.max(startIdx, endIdx)).trim();
  }

  if (!result.A && !result.B) return null;
  return result;
}

function parseOptions(optionsStr: string): { A: string; B: string; C: string; D: string } {
  const result = { A: "", B: "", C: "", D: "" };
  if (!optionsStr) return result;
  
  const optionRegex = /(?:^|[\s\n(])([A-D])\s*[.)\-:]\s*/gi;
  let match;
  const matches: { label: "A" | "B" | "C" | "D"; index: number; length: number }[] = [];
  
  while ((match = optionRegex.exec(optionsStr)) !== null) {
    matches.push({
      label: match[1].toUpperCase() as "A" | "B" | "C" | "D",
      index: match.index,
      length: match[0].length
    });
  }
  
  if (matches.length > 0) {
    matches.sort((a, b) => a.index - b.index);
    for (let i = 0; i < matches.length; i++) {
      const current = matches[i];
      const startIdx = current.index + current.length;
      const endIdx = (i + 1 < matches.length) ? matches[i + 1].index : optionsStr.length;
      result[current.label] = optionsStr.substring(startIdx, endIdx).trim();
    }
  } else {
    const lines = optionsStr.split("\n").map(l => l.trim()).filter(Boolean);
    if (lines.length >= 1) result.A = lines[0];
    if (lines.length >= 2) result.B = lines[1];
    if (lines.length >= 3) result.C = lines[2];
    if (lines.length >= 4) result.D = lines[3];
  }
  
  return result;
}

function normalizeCompetence(val?: string): "Reading" | "Listening" | "Writing" {
  const norm = normalizeCompetency(val);
  if (norm === "listening") return "Listening";
  if (norm === "writing") return "Writing";
  return "Reading";
}


function normalizeCorrectAnswer(value?: string): string {
  if (!value) return "";
  const cleaned = value.trim().toUpperCase();
  
  if (cleaned.startsWith("OPTION")) {
    const after = cleaned.substring(6).trim();
    if (after && ["A", "B", "C", "D"].includes(after.charAt(0))) {
      return after.charAt(0);
    }
  }
  const match = /([A-D])/.exec(cleaned);
  if (match && match[1]) {
    return match[1];
  }
  return "";
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

const ImageUploaderWidget = ({
  label,
  file,
  existingUrl,
  onChange
}: {
  label: string;
  file: File | LocalFileMetadata | null;
  existingUrl?: string;
  onChange: (file: File | null) => void;
}) => {
  const { t } = useTranslation();
  const [dragActive, setDragActive] = useState(false);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const f = e.dataTransfer.files[0];
      if (f.type.startsWith("image/")) {
        onChange(f);
      }
    }
  };

  const isMetadata = file && ("isMetadata" in file);
  const previewUrl = file && !isMetadata ? URL.createObjectURL(file as File) : existingUrl;

  return (
    <div className="space-y-1.5 w-full">
      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide block">{label}</span>
      {isMetadata ? (
        <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl space-y-2 relative animate-in fade-in duration-200">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <span className="font-bold text-gray-800 text-[10px] block leading-tight">{t("importer.localImageRequired")}</span>
              <p className="text-[9px] text-gray-500 leading-normal">
                {t("importer.reSelectImageMsg", { name: file.name, size: (file.size / 1024).toFixed(1) })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 justify-end">
            <label className="py-1 px-2.5 bg-amber-500 text-white hover:bg-amber-600 rounded-lg cursor-pointer shadow-sm text-[10px] font-bold flex items-center gap-1 transition-all">
              <Upload className="w-3" />
              <span>{t("importer.selectFile")}</span>
              <input 
                type="file" 
                accept="image/png, image/jpeg, image/jpg, image/webp" 
                className="hidden" 
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) onChange(e.target.files[0]);
                }} 
              />
            </label>
            <button 
              type="button"
              onClick={() => onChange(null)}
              className="py-1 px-2.5 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg transition-all text-[10px] font-bold flex items-center gap-1"
            >
              <Trash2 className="w-3 text-gray-500" />
              <span>{t("importer.remove")}</span>
            </button>
          </div>
        </div>
      ) : previewUrl ? (
        <div className="p-2 bg-gray-50 border border-gray-100 rounded-xl relative group overflow-hidden">
          <img src={previewUrl} alt="Preview" className="w-full max-h-[140px] object-contain rounded-lg" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <label className="p-1.5 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-all cursor-pointer shadow-sm text-[10px] font-bold flex items-center gap-1">
              <Upload className="w-3 h-3" />
              Substituir
              <input 
                type="file" 
                accept="image/png, image/jpeg, image/jpg, image/webp" 
                className="hidden" 
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) onChange(e.target.files[0]);
                }} 
              />
            </label>
            <button 
              type="button"
              onClick={() => onChange(null)}
              className="p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all shadow-sm text-[10px] font-bold flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" />
              Remover
            </button>
          </div>
          {file && (
            <div className="mt-1.5 flex justify-between items-center px-1">
              <span className="text-[9px] text-gray-400 font-semibold truncate max-w-[130px]">{file.name}</span>
              <span className="text-[9px] text-gray-400 font-bold">({((file as File).size / 1024).toFixed(1)} KB)</span>
            </div>
          )}
        </div>
      ) : (
        <div
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200 cursor-pointer relative ${
            dragActive 
              ? "border-purple-400 bg-purple-50/20" 
              : "border-gray-100 bg-gray-50/50 hover:border-gray-200"
          }`}
        >
          <Image className="w-5 h-5 text-gray-400 mx-auto mb-1.5" />
          <span className="text-[10px] font-bold text-gray-700 block">{t("importer.dragDropImage")}</span>
          <span className="text-[8px] text-gray-400 mt-0.5 block">{t("importer.dragDropImageFormats")}</span>
          <input 
            type="file" 
            accept="image/png, image/jpeg, image/jpg, image/webp" 
            className="absolute inset-0 opacity-0 cursor-pointer" 
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) onChange(e.target.files[0]);
            }} 
          />
        </div>
      )}
    </div>
  );
};

const AudioUploaderWidget = ({
  label,
  file,
  existingUrl,
  associatedAudioName,
  onChange
}: {
  label: string;
  file: File | LocalFileMetadata | null;
  existingUrl?: string;
  associatedAudioName?: string;
  onChange: (file: File | null) => void;
}) => {
  const { t } = useTranslation();
  const [dragActive, setDragActive] = useState(false);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const f = e.dataTransfer.files[0];
      if (f.type.startsWith("audio/") || f.name.endsWith(".mp3") || f.name.endsWith(".wav")) {
        onChange(f);
      }
    }
  };

  const isMetadata = file && ("isMetadata" in file);
  const previewUrl = file && !isMetadata ? URL.createObjectURL(file as File) : existingUrl;
  const audioName = file ? file.name : (associatedAudioName || "Áudio da questão");

  return (
    <div className="space-y-1.5 w-full">
      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide block">{label}</span>
      {isMetadata ? (
        <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl space-y-2 relative animate-in fade-in duration-200">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <span className="font-bold text-gray-800 text-[10px] block leading-tight">Áudio local necessário</span>
              <p className="text-[9px] text-gray-500 leading-normal">
                Selecione novamente: <strong className="text-gray-700">{file.name}</strong> ({((file as LocalFileMetadata).size / 1024 / 1024).toFixed(2)} MB)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 justify-end">
            <label className="py-1 px-2.5 bg-amber-500 text-white hover:bg-amber-600 rounded-lg cursor-pointer shadow-sm text-[10px] font-bold flex items-center gap-1 transition-all">
              <Upload className="w-3" />
              <span>{t("importer.selectAudio")}</span>
              <input 
                type="file" 
                accept="audio/mp3, audio/wav, .mp3, .wav" 
                className="hidden" 
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) onChange(e.target.files[0]);
                }} 
              />
            </label>
            <button 
              type="button"
              onClick={() => onChange(null)}
              className="py-1 px-2.5 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg transition-all text-[10px] font-bold flex items-center gap-1"
            >
              <Trash2 className="w-3 text-gray-500" />
              <span>{t("importer.remove")}</span>
            </button>
          </div>
        </div>
      ) : previewUrl ? (
        <div className="p-3 bg-blue-50/30 border border-blue-100/50 rounded-xl space-y-2 relative">
          <div className="flex items-center justify-between gap-1.5 flex-wrap">
            <div className="flex items-center gap-1.5 min-w-0">
              <Music className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <span className="font-bold text-gray-700 text-[10px] truncate max-w-[150px]">{audioName}</span>
              {file && <span className="text-[9px] text-gray-400">({((file as File).size / 1024 / 1024).toFixed(2)} MB)</span>}
            </div>
            <div className="flex items-center gap-1.5">
              <label className="py-1 px-2 bg-white text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 cursor-pointer shadow-sm text-[10px] font-bold flex items-center gap-1">
                <Upload className="w-3.5 h-3.5 text-gray-400" />
                <span>{t("common.replace", "Replace audio")}</span>
                <input 
                  type="file" 
                  accept="audio/mp3, audio/wav, .mp3, .wav" 
                  className="hidden" 
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) onChange(e.target.files[0]);
                  }} 
                />
              </label>
              <button 
                type="button"
                onClick={() => onChange(null)}
                className="py-1 px-2 bg-red-50 text-red-600 border border-red-100 rounded-lg hover:bg-red-100 transition-all text-[10px] font-bold flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                <span>{t("newQuestion.removeAudio")}</span>
              </button>
            </div>
          </div>
          <AudioPlayerWithSpeed src={previewUrl} compact className="w-full" />
        </div>
      ) : (
        <div
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200 cursor-pointer relative ${
            dragActive 
              ? "border-blue-400 bg-blue-50/20" 
              : "border-gray-100 bg-gray-50/50 hover:border-gray-200"
          }`}
        >
          <Volume2 className="w-5 h-5 text-gray-400 mx-auto mb-1.5" />
          <span className="text-[10px] font-bold text-gray-700 block">{t("importer.dragDropAudio")}</span>
          <span className="text-[8px] text-gray-400 mt-0.5 block">{t("importer.dragDropAudioFormats")}</span>
          <input 
            type="file" 
            accept="audio/mp3, audio/wav, .mp3, .wav" 
            className="absolute inset-0 opacity-0 cursor-pointer" 
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) onChange(e.target.files[0]);
            }} 
          />
        </div>
      )}
    </div>
  );
};

const doesQuestionRequireOptionImages = (q: any) => {
  if (!q) return false;
  if (q.requiresOptionImages === true) return true;
  const section = String(q.section || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  if (section.includes("dialogues associes a des images") || section === "section_a") return true;
  return false;
};

const matchImageFileToQuestionOption = (
  file: File,
  questions: any[]
): { temporaryImportId: string; optionLabel: "A" | "B" | "C" | "D" } | null => {
  const filename = file.name.toLowerCase().trim();
  const nameWithoutExt = filename.replace(/\.(png|jpg|jpeg|webp)$/i, "");
  
  // Find option letter at the end of the name
  const optMatch = nameWithoutExt.match(/_?([a-d])$/i);
  if (!optMatch) return null;
  const optionLabel = optMatch[1].toUpperCase() as "A" | "B" | "C" | "D";
  
  const prefix = nameWithoutExt.substring(0, nameWithoutExt.length - optMatch[0].length).trim();
  const optionImageQuestions = questions.filter(doesQuestionRequireOptionImages);

  // Pattern 5: A.png, B.png, C.png, D.png (if exactly one question requires option images)
  if (prefix === "" && optionImageQuestions.length === 1) {
    return {
      temporaryImportId: optionImageQuestions[0].temporaryImportId,
      optionLabel
    };
  }

  // Strip conversation, q, questao
  const cleanPrefix = prefix.replace(/^(conversation|q|questao)\s*/i, "").trim();
  const qNum = parseInt(cleanPrefix, 10);
  if (!isNaN(qNum)) {
    // Find the question matching this number
    const matchedQ = optionImageQuestions.find(q => q.importOrder === qNum || q.questionNumber === qNum || q.rawIndex === qNum);
    if (matchedQ) {
      return {
        temporaryImportId: matchedQ.temporaryImportId,
        optionLabel
      };
    }
  }
  
  return null;
};

function extractQuestionNumberFromAudioFile(fileName: string): number | null {
  const normalized = fileName.toLowerCase();

  const patterns = [
    /conversation[_\-\s]*(\d+)/,
    /dialogue[_\-\s]*(\d+)/,
    /question[_\-\s]*(\d+)/,
    /questao[_\-\s]*(\d+)/,
    /questão[_\-\s]*(\d+)/,
    /audio[_\-\s]*(\d+)/,
    /q[_\-\s]*(\d+)/,
    /^(\d+)[_\-\s.]/
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match?.[1]) return Number(match[1]);
  }

  return null;
}

export default function ImportQuestions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = (user as any)?.id as string | undefined;

  const initComp = (searchParams.get("comp") || localStorage.getItem("tef_import_comp")) as any || null;
  const initStep = parseInt(searchParams.get("step") || localStorage.getItem("tef_import_step") || "1", 10);
  const initTemplate = searchParams.get("template") || localStorage.getItem("tef_import_template") || "";

  const [selectedCompetence, setSelectedCompetence] = useState<"Reading" | "Listening" | "Writing" | null>(initComp);
  const [inputText, setInputText] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [audioFiles, setAudioFiles] = useState<File[]>([]);
  const [batchImageFiles, setBatchImageFiles] = useState<File[]>([]);
  const [manualAudioMappings, setManualAudioMappings] = useState<Record<string, File | null>>({});
  const [resolvedAudioMappings, setResolvedAudioMappings] = useState<Record<string, {
    file: File | null;
    method: "filename" | "natural_order" | "manual" | "none";
  }>>({});
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  
  // Custom Alerts / Toasts Local Feedback
  const [localToast, setLocalToast] = useState<string | null>(null);
  
  // Wizard Stepper States
  const [currentStep, setCurrentStep] = useState<number>(initStep);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initTemplate);

  // Auxiliary support image for CE Lecture rapide
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>("");
  const [importSubSection, setImportSubSection] = useState<string>("lecture_rapide_textes");
  const [isManualSubtypeSelected, setIsManualSubtypeSelected] = useState<boolean>(false);
  const [allowPartialImport, setAllowPartialImport] = useState<boolean>(true);

  // Draft persistence states
  const [draftStatus, setDraftStatus] = useState<"idle" | "saving" | "restored" | "save_failed">("idle");
  const [showRestorePrompt, setShowRestorePrompt] = useState<boolean>(false);

  // Strict import states (declared early so draft hooks can reference them)
  const [blindMode, setBlindMode] = useState(false);
  const [pendingMedia, setPendingMedia] = useState<Record<string, {
    supportImageFile: File | null;
    audioFile: File | null;
    optionImages: {
      A: File | null;
      B: File | null;
      C: File | null;
      D: File | null;
    };
  }>>({});

  const [validationResult, setValidationResult] = useState<{
    success: boolean;
    totalCount: number;
    validCount: number;
    invalidCount: number;
    duplicateCount?: number;
    validQuestions: any[];
    invalidQuestions: any[];
    allQuestions: any[];
    uniqueCompetencies: string[];
    isSingleCompetency: boolean;
    detectedCompetency: string | null;
    audioWarnings?: string;
    generalErrors?: string[];
  } | null>(null);

  const resetImportPage = () => {
    setSelectedCompetence(null);
    setCurrentStep(1);
    setSelectedTemplateId("");
    setInputText("");
    setFile(null);
    setAudioFiles([]);
    setBatchImageFiles([]);
    setManualAudioMappings({});
    setResolvedAudioMappings({});
    setValidationResult(null);
    setExpandedIndex(null);
    setSelectedImageFile(null);
    setImagePreviewUrl("");
    setImportSubSection("lecture_rapide_textes");
    setIsManualSubtypeSelected(false);
    setPendingMedia({});
    setDraftStatus("idle");
    setShowRestorePrompt(false);

    localStorage.removeItem("tef_import_comp");
    localStorage.removeItem("tef_import_step");
    localStorage.removeItem("tef_import_template");
    localStorage.removeItem("tef_import_questions_state");
    clearImportDraft(userId, "reset_import_page");

    navigate("/import-questions", { replace: true });
  };

  useEffect(() => {
    if (selectedCompetence) {
      setIsManualSubtypeSelected(false);
      const savedTemplate = localStorage.getItem("tef_import_template");
      if (savedTemplate) {
        setSelectedTemplateId(savedTemplate);
      } else {
        const templates = 
          selectedCompetence === "Reading" ? CE_IMPORT_TEMPLATES :
          selectedCompetence === "Listening" ? CO_IMPORT_TEMPLATES :
          EE_IMPORT_TEMPLATES;
        if (templates.length > 0) {
          setSelectedTemplateId(templates[0].id);
        }
      }
    } else {
      setSelectedTemplateId("");
    }
  }, [selectedCompetence]);
  // ─── Ref para capturar o estado mais recente sem closure stale ─────────────
  // IMPORTANTE: Este ref é atualizado em cada render via useEffect sem deps.
  // Todos os handlers de eventos de ciclo de vida (visibilitychange, blur,
  // pagehide, beforeunload, unmount) devem ler deste ref para evitar salvar
  // estado antigo.
  const draftStateRef = useRef<{
    currentStep: number;
    selectedCompetence: "Reading" | "Listening" | "Writing" | null;
    selectedTemplateId: string;
    inputText: string;
    importSubSection: string;
    isManualSubtypeSelected: boolean;
    allowPartialImport: boolean;
    blindMode: boolean;
    validationResult: any;
    pendingMedia: Record<string, any>;
    manualAudioMappings: Record<string, any>;
    resolvedAudioMappings: Record<string, { file: File | null; method: string }>;
    audioFiles: File[];
    batchImageFiles: File[];
    selectedImageFile: File | null;
  }>({
    currentStep,
    selectedCompetence,
    selectedTemplateId,
    inputText,
    importSubSection,
    isManualSubtypeSelected,
    allowPartialImport,
    blindMode,
    validationResult: null,
    pendingMedia: {},
    manualAudioMappings: {},
    resolvedAudioMappings: {},
    audioFiles: [],
    batchImageFiles: [],
    selectedImageFile: null,
  });

  // Persistência em searchParams e localStorage
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedCompetence) {
      params.set("comp", selectedCompetence);
      localStorage.setItem("tef_import_comp", selectedCompetence);
    } else {
      params.delete("comp");
      localStorage.removeItem("tef_import_comp");
    }
    params.set("step", currentStep.toString());
    localStorage.setItem("tef_import_step", currentStep.toString());
    if (selectedTemplateId) {
      params.set("template", selectedTemplateId);
      localStorage.setItem("tef_import_template", selectedTemplateId);
    } else {
      params.delete("template");
      localStorage.removeItem("tef_import_template");
    }
    setSearchParams(params, { replace: true });
  }, [selectedCompetence, currentStep, selectedTemplateId, setSearchParams]);

  // ─── Draft ref: mantido sempre atualizado (sem array de deps = roda em todo render) ──
  // Não coloque deps aqui: o objetivo é capturar QUALQUER mudança de estado.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    draftStateRef.current = {
      currentStep,
      selectedCompetence,
      selectedTemplateId,
      inputText,
      importSubSection,
      isManualSubtypeSelected,
      allowPartialImport,
      blindMode,
      validationResult,
      pendingMedia,
      manualAudioMappings,
      resolvedAudioMappings,
      audioFiles,
      batchImageFiles,
      selectedImageFile,
    };
  });

  // ─── saveDraftNow: usa ref para nunca capturar estado antigo ───────────────────
  const saveDraftNow = useCallback((reason: string) => {
    const s = draftStateRef.current;
    console.log("[IMPORT_DRAFT_SAVE_NOW]", {
      reason,
      step: s.currentStep,
      competency: s.selectedCompetence,
      hasRawJson: Boolean(s.inputText),
      rawJsonLength: s.inputText?.length || 0,
      questionCount: s.validationResult?.totalCount || 0,
      hasResolvedAudio: Object.keys(s.resolvedAudioMappings || {}).length,
    });
    saveImportDraft(userId, {
      ...s,
      resolvedAudioMappings: s.resolvedAudioMappings,
    }, { immediate: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ─── Draft: autosave com debounce 500ms (para todos os campos serializáveis) ─
  useEffect(() => {
    const timer = setTimeout(() => {
      setDraftStatus("saving");
      try {
        saveImportDraft(userId, {
          currentStep,
          selectedCompetence,
          selectedTemplateId,
          inputText,
          importSubSection,
          isManualSubtypeSelected,
          allowPartialImport,
          blindMode,
          validationResult,
          pendingMedia,
          manualAudioMappings,
          resolvedAudioMappings,
          audioFiles,
          batchImageFiles,
          selectedImageFile
        });
        setDraftStatus("idle");
      } catch (e) {
        console.error("[IMPORT_DRAFT_AUTOSAVE_FAILED]", e);
        setDraftStatus("save_failed");
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [
    currentStep, selectedCompetence, selectedTemplateId, inputText,
    importSubSection, isManualSubtypeSelected, allowPartialImport, blindMode,
    validationResult, pendingMedia, manualAudioMappings, resolvedAudioMappings,
    audioFiles, batchImageFiles, selectedImageFile, userId
  ]);

  // ─── Draft: handler unificado para todos os eventos críticos de ciclo de vida ─
  // Usa saveDraftNow que lê de draftStateRef.current — nunca closure stale.
  // Cobre: visibilitychange, window.blur, pagehide, beforeunload, unmount.
  useEffect(() => {
    const handleHide = () => {
      if (document.visibilityState === "hidden") {
        saveDraftNow("visibilitychange_hidden");
      }
    };
    const handleBlur = () => saveDraftNow("window_blur");
    const handlePageHide = () => saveDraftNow("pagehide");
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      saveDraftNow("beforeunload");
      const s = draftStateRef.current;
      const hasDraftContent = Boolean(
        s.inputText || (s.validationResult?.totalCount && s.validationResult.totalCount > 0)
      );
      if (hasDraftContent) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    document.addEventListener("visibilitychange", handleHide);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      saveDraftNow("component_unmount");
      document.removeEventListener("visibilitychange", handleHide);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveDraftNow]);

  // ─── Draft: verificar rascunho salvo ao montar ─────────────────────────────
  useEffect(() => {
    const { draft } = getImportDraft(userId);
    if (!draft) return;
    // Only show restore prompt if there is meaningful data (not just step 1 with no content)
    const hasContent = Boolean(
      draft.inputText ||
      draft.validationResult?.totalCount ||
      (draft.currentStep && draft.currentStep > 1)
    );
    if (!hasContent) {
      clearImportDraft(userId, "empty_draft");
      return;
    }
    setShowRestorePrompt(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRestoreDraft = () => {
    const { draft } = getImportDraft(userId);
    if (!draft) {
      setShowRestorePrompt(false);
      return;
    }
    setCurrentStep(draft.currentStep || 1);
    setSelectedCompetence(draft.selectedCompetence);
    setSelectedTemplateId(draft.selectedTemplateId || "");
    setInputText(draft.inputText || "");
    setImportSubSection(draft.importSubSection || "lecture_rapide_textes");
    setIsManualSubtypeSelected(draft.isManualSubtypeSelected || false);
    setAllowPartialImport(draft.allowPartialImport !== false);
    setBlindMode(draft.blindMode || false);
    if (draft.validationResult) setValidationResult(draft.validationResult);

    // Restore pending media metadata
    if (draft.pendingMedia) {
      const restoredMedia: Record<string, any> = {};
      for (const [qId, m] of Object.entries(draft.pendingMedia)) {
        restoredMedia[qId] = {
          supportImageFile: m.supportImageFile || null,
          audioFile: m.audioFile || null,
          optionImages: {
            A: m.optionImages?.A || null,
            B: m.optionImages?.B || null,
            C: m.optionImages?.C || null,
            D: m.optionImages?.D || null,
          }
        };
      }
      setPendingMedia(restoredMedia);
    }
    // Restore manual audio mappings metadata
    if (draft.manualAudioMappings) {
      setManualAudioMappings(draft.manualAudioMappings as any);
    }

    setShowRestorePrompt(false);
    setDraftStatus("restored");
    console.log("[IMPORT_DRAFT_RESTORED]", {
      step: draft.currentStep,
      competency: draft.selectedCompetence,
      questionCount: draft.validationResult?.totalCount || 0
    });
    setTimeout(() => setDraftStatus("idle"), 4000);
  };

  const handleDiscardDraft = () => {
    clearImportDraft(userId, "user_discarded");
    setShowRestorePrompt(false);
    setDraftStatus("idle");
  };

  // Pre-import duplicate validation states
  const [existingQuestions, setExistingQuestions] = useState<any[]>([]);
  const [existingHashes, setExistingHashes] = useState<Set<string>>(new Set());
  const [calculatedHashes, setCalculatedHashes] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    async function loadExistingQuestions() {
      try {
        const questions = await questionService.getQuestions();
        if (!active) return;
        setExistingQuestions(questions || []);
        
        // Pre-compute hashes for all existing questions
        const hashes = new Set<string>();
        
        await Promise.all(((questions || []) as any[]).map(async (q: any) => {
          const h = q.metadata?.importFingerprintHash || q.duplicateKey || (q as any).duplicate_key;
          if (h && typeof h === "string") {
            hashes.add(h);
          } else {
            try {
              const computed = await buildQuestionFingerprintHash(q);
              hashes.add(computed);
            } catch (e) {
              console.error("Failed to compute hash for existing question:", q.id, e);
            }
          }
        }));
        
        if (active) {
          setExistingHashes(hashes);
          console.log("[IMPORT_EXISTING_FINGERPRINTS_LOADED]", hashes.size);
        }
      } catch (err) {
        console.error("Erro ao carregar questões para validação de duplicidade:", err);
      }
    }
    loadExistingQuestions();
    return () => { active = false; };
  }, []);

  // Compute batch hashes asynchronously
  useEffect(() => {
    if (!validationResult?.allQuestions) return;
    
    let active = true;
    async function computeBatchHashes() {
      const hashesMap: Record<string, string> = {};
      const questions = validationResult.allQuestions;
      
      await Promise.all(questions.map(async (q) => {
        const stableQ = getStableQuestionForFingerprint(q, pendingMedia, resolvedAudioMappings);
        try {
          const h = await buildQuestionFingerprintHash(stableQ);
          hashesMap[q.temporaryImportId] = h;
        } catch (e) {
          console.error("Failed to compute hash for import question:", q.temporaryImportId, e);
        }
      }));
      
      if (active) {
        setCalculatedHashes(prev => {
          const hasChanges = Object.keys(hashesMap).some(k => prev[k] !== hashesMap[k]) ||
                             Object.keys(prev).length !== Object.keys(hashesMap).length;
          return hasChanges ? hashesMap : prev;
        });
      }
    }
    
    computeBatchHashes();
    return () => { active = false; };
  }, [validationResult?.allQuestions, pendingMedia, resolvedAudioMappings, selectedCompetence]);

  // States for targeted Ctrl+V paste boxes (Audio and Images)
  const [pastedAudioTargetQId, setPastedAudioTargetQId] = useState<string>("");
  const [pastedImageTargetQId, setPastedImageTargetQId] = useState<string>("");
  const [pastedImageTargetOption, setPastedImageTargetOption] = useState<"A" | "B" | "C" | "D">("A");

  const handleBatchImageUpload = (files: File[]) => {
    if (!validationResult) return;
    
    const allowedExtensions = /\.(png|jpg|jpeg|webp)$/i;
    const imageFiles = files.filter(f => allowedExtensions.test(f.name));
    
    if (imageFiles.length === 0) return;

    setBatchImageFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      const filteredNew = imageFiles.filter(f => !existingNames.has(f.name));
      return [...prev, ...filteredNew];
    });

    setPendingMedia(prev => {
      const updated = { ...prev };
      let mappedCount = 0;
      
      imageFiles.forEach(file => {
        const match = matchImageFileToQuestionOption(file, validationResult.allQuestions);
        if (match) {
          const { temporaryImportId, optionLabel } = match;
          if (!updated[temporaryImportId]) {
            updated[temporaryImportId] = {
              supportImageFile: null,
              audioFile: null,
              optionImages: { A: null, B: null, C: null, D: null }
            };
          }
          
          updated[temporaryImportId] = {
            ...updated[temporaryImportId],
            optionImages: {
              ...updated[temporaryImportId].optionImages,
              [optionLabel]: file
            }
          };
          
          mappedCount++;
          console.log("[IMPORT_CO_IMAGE_MAPPED]", {
            temporaryImportId,
            optionLabel,
            fileName: file.name
          });
        }
      });

      if (mappedCount > 0) {
        showLocalToast(`${mappedCount} imagens mapeadas automaticamente.`);
      }
      return updated;
    });
  };


  const revalidateQuestionsWithMedia = (questions: any[], mediaState: typeof pendingMedia) => {
    const batchHashes = new Set<string>();

    return questions.map(q => {
      const qNum = q.importOrder || q.questionNumber || q.rawIndex;
      const mediaForQ = mediaState[q.temporaryImportId];
      
      const cleanErrors = q.errors.filter((err: string) => {
        return !err.includes("imagem da alternativa") && 
               !err.includes("optionAImage") && 
               !err.includes("optionBImage") && 
               !err.includes("optionCImage") && 
               !err.includes("optionDImage") && 
               !err.includes("áudio da questão") && 
               !err.includes("Áudio da questão") &&
               !err.includes("OPTION_A_IMAGE") &&
               !err.includes("OPTION_B_IMAGE") &&
               !err.includes("OPTION_C_IMAGE") &&
               !err.includes("OPTION_D_IMAGE") &&
               !err.includes("AUDIO:") &&
               !err.includes("Imagem da alternativa");
      });

      const cleanWarnings = q.warnings.filter((warn: string) => {
        return !warn.includes("exige uma imagem/gráfico") && 
               !warn.includes("normalmente exige uma imagem") && 
               !warn.includes("sem áudio") && 
               !warn.includes("alternativas por imagem") &&
               !warn.includes("Esta questão já foi importada anteriormente") &&
               !warn.includes("Esta questão está repetida neste lote de importação");
      });

      // 1. CE - Lecture rapide
      if (selectedCompetence === "Reading" && q.section === "Lecture rapide de textes et de graphiques") {
        const hasImageFile = mediaForQ?.supportImageFile !== null && mediaForQ?.supportImageFile !== undefined;
        const hasExistingUrl = isPermanentMediaUrl(q.imageUrl);
        if (!hasImageFile && !hasExistingUrl) {
          cleanWarnings.push("Esta seção normalmente exige uma imagem ou gráfico. Você pode importar agora e anexar a imagem depois na edição.");
        }
      }

      // 2. CO - Dialogues associés à des images
      if (selectedCompetence === "Listening" && q.section === "Dialogues associés à des images") {
        const labels = ["A", "B", "C", "D"] as const;
        labels.forEach(label => {
          const hasPendingImage = mediaForQ?.optionImages?.[label] !== null && mediaForQ?.optionImages?.[label] !== undefined;
          const safeOptions = Array.isArray(q.options) ? q.options : normalizeImportedQuestionsPayload(q.options) || normalizeQuestionOptions(q);
          const optObj = Array.isArray(safeOptions) ? safeOptions.find((o: any) => o.label === label) : null;
          const existingUrl = optObj?.imageUrl || optObj?.mediaId || optObj?.image || q[`option${label}Image`] || q[`image${label}`];
          const hasExistingUrl = isPermanentMediaUrl(existingUrl);

          if (!hasPendingImage && !hasExistingUrl) {
            cleanErrors.push(`Questão ${qNum}: Imagem da alternativa ${label} ausente ou não permanente.`);
          }
        });

        const hasPendingAudio = mediaForQ?.audioFile !== null && mediaForQ?.audioFile !== undefined;
        const mappedAudio = resolvedAudioMappings[q.temporaryImportId]?.file;
        const hasMappedAudio = !!mappedAudio;
        const existingAudioUrl = q.audioUrl || q.audio || (q as any).audio_url;
        const hasExistingAudio = isPermanentMediaUrl(existingAudioUrl);

        if (!hasPendingAudio && !hasMappedAudio && !hasExistingAudio) {
          cleanErrors.push(`Questão ${qNum}: o áudio da questão é obrigatório para a seção 'Dialogues associés à des images'.`);
        }
      }

      // 3. CO - Any other listening section (highly recommended/required audio)
      if (selectedCompetence === "Listening" && q.section !== "Dialogues associés à des images") {
        const hasPendingAudio = mediaForQ?.audioFile !== null && mediaForQ?.audioFile !== undefined;
        const mappedAudio = resolvedAudioMappings[q.temporaryImportId]?.file;
        const hasMappedAudio = !!mappedAudio;
        const existingAudioUrl = q.audioUrl || q.audio || (q as any).audio_url;
        const hasExistingAudio = isPermanentMediaUrl(existingAudioUrl);

        if (!hasPendingAudio && !hasMappedAudio && !hasExistingAudio) {
          cleanErrors.push(`Questão ${qNum}: o áudio da questão é obrigatório para questões de Compreensão Orale.`);
        }
      }

      // 4. Duplicate Validation using Fingerprint Hash
      let isDuplicate = false;
      let duplicateReason: "exists_in_database" | "duplicate_in_batch" | null = null;

      const hash = calculatedHashes[q.temporaryImportId];

      if (hash) {
        // A. Check against existing questions in database
        const dbDuplicate = existingHashes.has(hash);

        if (dbDuplicate) {
          isDuplicate = true;
          duplicateReason = "exists_in_database";
          cleanWarnings.push("Esta questão já foi importada anteriormente (já existe no Banco de Questões).");
        } else {
          // B. Check against the batch itself
          if (batchHashes.has(hash)) {
            isDuplicate = true;
            duplicateReason = "duplicate_in_batch";
            cleanWarnings.push("Esta questão está repetida neste lote de importação.");
          } else {
            batchHashes.add(hash);
          }
        }
      }

      return {
        ...q,
        errors: cleanErrors,
        warnings: cleanWarnings,
        isDuplicate,
        duplicateReason,
        isValid: cleanErrors.length === 0 && !isDuplicate
      };
    });
  };

  // Keep target selections synchronized when validationResult changes
  useEffect(() => {
    if (validationResult?.allQuestions && validationResult.allQuestions.length > 0) {
      const currentAudioTargetExists = validationResult.allQuestions.some(q => q.temporaryImportId === pastedAudioTargetQId);
      if (!pastedAudioTargetQId || !currentAudioTargetExists) {
        setPastedAudioTargetQId(validationResult.allQuestions[0].temporaryImportId);
      }

      const imageQuestions = validationResult.allQuestions.filter(doesQuestionRequireOptionImages);
      const currentImageTargetExists = imageQuestions.some(q => q.temporaryImportId === pastedImageTargetQId);
      if (imageQuestions.length > 0) {
        if (!pastedImageTargetQId || !currentImageTargetExists) {
          setPastedImageTargetQId(imageQuestions[0].temporaryImportId);
        }
      } else {
        setPastedImageTargetQId("");
      }
    } else {
      setPastedAudioTargetQId("");
      setPastedImageTargetQId("");
    }
  }, [validationResult?.allQuestions, pastedAudioTargetQId, pastedImageTargetQId]);

  // Paste handler callbacks
  const handleAudioFilePaste = (file: File, qId: string) => {
    setAudioFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      if (existingNames.has(file.name)) return prev;
      return [...prev, file];
    });

    setManualAudioMappings(prev => ({
      ...prev,
      [qId]: file
    }));

    setPendingMedia(prev => {
      const qMedia = prev[qId] || {
        supportImageFile: null,
        audioFile: null,
        optionImages: { A: null, B: null, C: null, D: null }
      };
      return {
        ...prev,
        [qId]: {
          ...qMedia,
          audioFile: file
        }
      };
    });
  };

  const handleAudioUrlPaste = (url: string, qId: string) => {
    setValidationResult(prev => {
      if (!prev) return null;
      
      const updatedAllQuestions = prev.allQuestions.map(q => {
        if (q.temporaryImportId === qId) {
          return {
            ...q,
            audioUrl: url,
            audio: url,
            hasAudio: true
          };
        }
        return q;
      });

      const revalidated = revalidateQuestionsWithMedia(updatedAllQuestions, pendingMedia);
      const validQuestions = revalidated.filter(q => q.isValid);
      const invalidQuestions = revalidated.filter(q => !q.isValid);

      return {
        ...prev,
        validCount: validQuestions.length,
        invalidCount: invalidQuestions.length,
        validQuestions,
        invalidQuestions,
        allQuestions: revalidated,
        success: validQuestions.length > 0
      };
    });
  };

  const handleImageFilePaste = (file: File, qId: string, optionLabel: "A" | "B" | "C" | "D") => {
    setBatchImageFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      if (existingNames.has(file.name)) return prev;
      return [...prev, file];
    });

    setPendingMedia(prev => {
      const qMedia = prev[qId] || {
        supportImageFile: null,
        audioFile: null,
        optionImages: { A: null, B: null, C: null, D: null }
      };
      return {
        ...prev,
        [qId]: {
          ...qMedia,
          optionImages: {
            ...qMedia.optionImages,
            [optionLabel]: file
          }
        }
      };
    });
  };

  const handleImageUrlPaste = (url: string, qId: string, optionLabel: "A" | "B" | "C" | "D") => {
    setValidationResult(prev => {
      if (!prev) return null;

      const updatedAllQuestions = prev.allQuestions.map(q => {
        if (q.temporaryImportId !== qId) return q;

        const safeOptions = Array.isArray(q.options) 
          ? [...q.options] 
          : [...(normalizeImportedQuestionsPayload(q.options) || normalizeQuestionOptions(q))];
        
        const updatedOptions = safeOptions.map((o: any) => 
          o.label === optionLabel 
            ? { ...o, imageUrl: url, image: url } 
            : o
        );

        return {
          ...q,
          options: updatedOptions,
          [`option${optionLabel}Image`]: url,
          [`image${optionLabel}`]: url
        };
      });

      const revalidated = revalidateQuestionsWithMedia(updatedAllQuestions, pendingMedia);
      const validQuestions = revalidated.filter(q => q.isValid);
      const invalidQuestions = revalidated.filter(q => !q.isValid);

      return {
        ...prev,
        validCount: validQuestions.length,
        invalidCount: invalidQuestions.length,
        validQuestions,
        invalidQuestions,
        allQuestions: revalidated,
        success: validQuestions.length > 0
      };
    });
  };


  useEffect(() => {
    if (!validationResult || validationResult.generalErrors) return;

    const updatedAllQuestions = revalidateQuestionsWithMedia(validationResult.allQuestions, pendingMedia);
    const validQuestions = updatedAllQuestions.filter(q => q.isValid);
    const duplicateQuestions = updatedAllQuestions.filter(q => q.isDuplicate && q.errors.length === 0);
    const invalidQuestions = updatedAllQuestions.filter(q => q.errors.length > 0);

    const hasChanges = JSON.stringify(updatedAllQuestions.map(q => ({ id: q.rawIndex, valid: q.isValid, isDuplicate: q.isDuplicate, errCount: q.errors.length, warnCount: q.warnings.length }))) !== 
                       JSON.stringify(validationResult.allQuestions.map(q => ({ id: q.rawIndex, valid: q.isValid, isDuplicate: q.isDuplicate, errCount: q.errors.length, warnCount: q.warnings.length })));

    if (hasChanges) {
      setValidationResult(prev => {
        if (!prev) return null;
        return {
          ...prev,
          validCount: validQuestions.length,
          invalidCount: invalidQuestions.length,
          duplicateCount: duplicateQuestions.length,
          validQuestions,
          invalidQuestions,
          allQuestions: updatedAllQuestions,
          success: validQuestions.length > 0
        };
      });
    }
  }, [pendingMedia, resolvedAudioMappings, validationResult?.allQuestions?.length, selectedCompetence, existingQuestions, calculatedHashes, existingHashes]);

  useEffect(() => {
    if (selectedCompetence !== "Listening" || !validationResult?.allQuestions) return;

    console.log("[CO_IMPORT_AUDIO_FILES_RECEIVED]", audioFiles.map(file => file.name));

    // 1. Natural Sort of all audio files
    const sortedFiles = [...audioFiles].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
    );

    const mappings: Record<string, { file: File | null; method: "filename" | "natural_order" | "manual" | "none" }> = {};
    const usedFileNames = new Set<string>();

    const questions = validationResult.allQuestions;

    // First Pass: Manual mappings
    questions.forEach(q => {
      const manualFile = manualAudioMappings[q.temporaryImportId];
      if (manualFile !== undefined) {
        mappings[q.temporaryImportId] = {
          file: manualFile,
          method: manualFile ? "manual" : "none"
        };
        if (manualFile) {
          usedFileNames.add(manualFile.name);
        }
      }
    });

    // Second Pass: Filename matching (for questions without manual mappings)
    questions.forEach(q => {
      if (mappings[q.temporaryImportId]) return; // Skip if already mapped manually

      // Find file that matches this question number
      const matchedFile = audioFiles.find(file => {
        if (usedFileNames.has(file.name)) return false;
        const qNum = extractQuestionNumberFromAudioFile(file.name);
        return qNum === q.importOrder || qNum === q.questionNumber || qNum === q.rawIndex;
      });

      if (matchedFile) {
        mappings[q.temporaryImportId] = {
          file: matchedFile,
          method: "filename"
        };
        usedFileNames.add(matchedFile.name);
      }
    });

    // Third Pass: Fallback Natural Order (for remaining unmatched questions)
    const remainingFiles = sortedFiles.filter(f => !usedFileNames.has(f.name));
    let fallbackIdx = 0;

    questions.forEach(q => {
      if (mappings[q.temporaryImportId]) return; // Skip if already mapped

      if (fallbackIdx < remainingFiles.length) {
        const file = remainingFiles[fallbackIdx];
        mappings[q.temporaryImportId] = {
          file,
          method: "natural_order"
        };
        usedFileNames.add(file.name);
        fallbackIdx++;
      } else {
        mappings[q.temporaryImportId] = {
          file: null,
          method: "none"
        };
      }
    });

    setResolvedAudioMappings(prev => {
      let hasChanges = false;
      const keys = Object.keys(mappings);
      if (Object.keys(prev).length !== keys.length) {
        hasChanges = true;
      } else {
        for (const key of keys) {
          if (prev[key]?.file !== mappings[key]?.file || prev[key]?.method !== mappings[key]?.method) {
            hasChanges = true;
            break;
          }
        }
      }
      return hasChanges ? mappings : prev;
    });

    // Update pendingMedia state reactively only if there is a change
    setPendingMedia(prev => {
      let hasChanges = false;
      const updated = { ...prev };
      questions.forEach(q => {
        const mapped = mappings[q.temporaryImportId];
        const prevQMedia = prev[q.temporaryImportId];
        const newAudioFile = mapped?.file || null;
        
        if (!prevQMedia || prevQMedia.audioFile !== newAudioFile) {
          hasChanges = true;
        }
        
        if (!updated[q.temporaryImportId]) {
          updated[q.temporaryImportId] = {
            supportImageFile: null,
            audioFile: null,
            optionImages: { A: null, B: null, C: null, D: null }
          };
        }
        updated[q.temporaryImportId] = {
          ...updated[q.temporaryImportId],
          audioFile: newAudioFile
        };
      });
      
      return hasChanges ? updated : prev;
    });

    // Mandatory Log B
    console.log("[CO_IMPORT_STEP_2_AUDIO_MAPPED]", questions.map(q => ({
      temporaryImportId: q.temporaryImportId,
      importOrder: q.importOrder,
      title: q.title,
      audioFileName: mappings[q.temporaryImportId]?.file?.name || null,
    })));

  }, [audioFiles, validationResult?.allQuestions, manualAudioMappings, selectedCompetence]);

  useEffect(() => {
    if (validationResult && currentStep === 3) {
      setCurrentStep(4);
    }
  }, [validationResult, currentStep]);

  const showLocalToast = (msg: string) => {
    setLocalToast(msg);
    setTimeout(() => {
      setLocalToast(null);
    }, 2500);
  };



  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  // The comprehensive validation and parsing controller
  const processText = (rawText: string) => {
    if (!selectedCompetence) return;
    setIsValidating(true);
    setValidationResult(null);
    setExpandedIndex(null);

    setTimeout(() => {
      let isJSON = false;
      let parsedJSON: any = null;
      const generalErrors: string[] = [];
      const parsedQuestions: any[] = [];
      let blocks: string[] = [];
      
      const cleanInput = rawText.trim();

      // 1. Format Detection
      if (cleanInput.startsWith("{") || cleanInput.startsWith("[")) {
        try {
          const rawParsed = JSON.parse(cleanInput);
          console.log("[IMPORT_RAW_JSON_PARSED]", rawParsed);
          parsedJSON = normalizeImportedQuestionsPayload(rawParsed);
          
          console.log("[IMPORT_NORMALIZED_QUESTIONS]", parsedJSON);
          console.log("[IMPORT_NORMALIZED_QUESTIONS_SUMMARY]", {
            competency: parsedJSON?.competency,
            count: parsedJSON?.questions?.length || 0,
            questions: parsedJSON?.questions?.map((q: any) => ({
              title: q.title,
              section: q.section,
              optionsIsArray: Array.isArray(q.options),
              optionsCount: q.options?.length || 0,
              requiresAudio: q.requiresAudio,
              requiresOptionImages: q.requiresOptionImages,
            })),
          });

          isJSON = true;
        } catch (e: any) {
          generalErrors.push(`Erro de sintaxe JSON: ${e.message}. Corrija a estrutura do seu arquivo.`);
        }
      }

      if (isJSON && parsedJSON) {
        // Structured JSON validations
        const competencyStr = parsedJSON.competency || "";
        if (!competencyStr) {
          generalErrors.push("O arquivo JSON deve declarar a competência raiz (ex: \"competency\": \"reading\").");
        } else {
          const normComp = normalizeCompetence(competencyStr);
          if (normComp !== selectedCompetence) {
            generalErrors.push("Este pacote contém mais de uma competência. Importe Reading, Listening e Writing separadamente.");
          }
        }

        const questionsArray = parsedJSON.questions;
        if (!questionsArray || !Array.isArray(questionsArray)) {
          generalErrors.push("O arquivo JSON deve conter o array \"questions\" na raiz.");
        } else if (questionsArray.length === 0) {
          generalErrors.push("O array de questões \"questions\" está vazio.");
        }

        if (generalErrors.length > 0) {
          setIsValidating(false);
          setValidationResult({
            success: false,
            totalCount: 0,
            validCount: 0,
            invalidCount: 0,
            duplicateCount: 0,
            validQuestions: [],
            invalidQuestions: [],
            allQuestions: [],
            uniqueCompetencies: [],
            isSingleCompetency: false,
            detectedCompetency: null,
            generalErrors
          });
          return;
        }

        // Validate each JSON question
        questionsArray.forEach((q: any, index: number) => {
          const qNum = index + 1;
          const tempId = q.temporaryImportId || q.id || (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `imported-question-${qNum}`);
          
          // Use centralized validation
          const validation = validateQuestion(q, { 
            competence: selectedCompetence, 
            section: q.section 
          });

          const errors = validation.errors.map(err => `Questão ${qNum}: ${err}`);
          const warnings: string[] = [];

          // Core property mappings for UI preview
          const title = (q.title || "").trim();
          const section = (q.section || "").trim();
          const instruction = (q.instruction || q.prompt || q.question || "").trim();
          const body = (q.body || q.text || "").trim();
          
          // Automatic difficulty
          const autoDiff = getAutomaticDifficulty({
            competency: selectedCompetence.toLowerCase(),
            section,
            audioType: q.audioType || ""
          });

          // Normalization logic for the UI object
          const normalized = normalizeQuestion(q, { competence: selectedCompetence });

          parsedQuestions.push({
            ...normalized,
            temporaryImportId: tempId,
            importOrder: qNum,
            questionNumber: qNum,
            originalImportIndex: index,
            title: title || `Questão JSON #${qNum}`,
            status: "active",
            isValid: validation.isValid,
            errors,
            warnings,
            rawIndex: qNum
          });
        });
      } else {
        // 2. Legacy Marcadores Block Parser
        const normalizedRawText = cleanInput
          .replace(/===\s*END\s*QUESTION\s*===\s*===\s*QUESTION\s*===/gi, "=== END QUESTION ===\n\n=== QUESTION ===")
          .replace(/(===\s*QUESTION\s*===)\s*(TITLE:)/gi, "$1\n$2")
          .replace(/(STATUS:\s*\w+)\s*(===\s*END\s*QUESTION\s*===)/gi, "$1\n$2")
          .replace(/===\s*QUESTION\s*===/gi, "\n=== QUESTION ===\n")
          .replace(/===\s*END\s*QUESTION\s*===/gi, "\n=== END QUESTION ===\n");

        const startPattern = /(?:===?\s*QUESTION\s*===?|---+\s*QUESTION\s*---+|QUESTION:)/gi;
        let match;
        const startIndices: number[] = [];
        const startLengths: number[] = [];
        
        while ((match = startPattern.exec(normalizedRawText)) !== null) {
          startIndices.push(match.index);
          startLengths.push(match[0].length);
        }
        
        blocks = [];
        if (startIndices.length === 0) {
          if (normalizedRawText.trim()) blocks.push(normalizedRawText.trim());
        } else {
          for (let i = 0; i < startIndices.length; i++) {
            const startIdx = startIndices[i] + startLengths[i];
            const endIdx = (i + 1 < startIndices.length) ? startIndices[i + 1] : normalizedRawText.length;
            let blockText = normalizedRawText.substring(startIdx, endIdx);
            
            const endMatch = /(?:===?\s*END\s+QUESTION\s*===?|---+\s*END\s+QUESTION\s*---+|===?\s*ENDQUESTION\s*===?)/i.exec(blockText);
            if (endMatch) {
              blockText = blockText.substring(0, endMatch.index);
            }
            blocks.push(blockText.trim());
          }
        }

        if (blocks.length === 0) {
          generalErrors.push("Nenhuma questão em formato legado detectada no texto.");
          setIsValidating(false);
          setValidationResult({
            success: false,
            totalCount: 0,
            validCount: 0,
            invalidCount: 0,
            duplicateCount: 0,
            validQuestions: [],
            invalidQuestions: [],
            allQuestions: [],
            uniqueCompetencies: [],
            isSingleCompetency: false,
            detectedCompetency: null,
            generalErrors
          });
          return;
        }

        blocks.forEach((block, index) => {
          const qNum = index + 1;
          const tempId = `imported-question-legacy-${qNum}`;
          const fields = parseBlock(block);
          
          // Map legacy fields to a temporary question object
          const tempQ: any = {
            title: fields.TITLE,
            competence: fields.COMPETENCY || selectedCompetence,
            section: fields.SECTION,
            instruction: fields.INSTRUCTION,
            question: fields.QUESTION || fields.PROMPT,
            text: fields.BODY || fields.TEXT,
            explanation: fields.EXPLANATION,
            correctAnswer: fields.CORRECT_ANSWER || fields.ANSWER,
            tags: fields.TAGS,
            subSection: (fields.SOUS_PARTIE || fields.AUDIO_TYPE || fields.TYPE_AUDIO || ""),
            options: fields.OPTIONS ? parseOptions(fields.OPTIONS) : extractOptionsFromFreeText(block)
          };

          // Use centralized validation
          const validation = validateQuestion(tempQ, { 
            competence: selectedCompetence, 
            section: tempQ.section 
          });

          const errors = validation.errors.map(err => `Questão ${qNum}: ${err}`);
          const warnings: string[] = [];

          // Normalization logic
          const normalized = normalizeQuestion(tempQ, { competence: selectedCompetence });

          // Listening specific: images for Section A
          let optImages: any = undefined;
          if (selectedCompetence === "Listening" && tempQ.section === "Dialogues associés à des images") {
            optImages = [
              { label: "A", text: "", imageUrl: fields.OPTION_A_IMAGE || "" },
              { label: "B", text: "", imageUrl: fields.OPTION_B_IMAGE || "" },
              { label: "C", text: "", imageUrl: fields.OPTION_C_IMAGE || "" },
              { label: "D", text: "", imageUrl: fields.OPTION_D_IMAGE || "" }
            ];
          }

          const hasAudioImported = selectedCompetence === "Listening" && !!(fields.AUDIO || fields.AUDIO_URL || fields.AUDIO_FILE);
          const finalAudioUrl = hasAudioImported ? (fields.AUDIO || fields.AUDIO_URL || fields.AUDIO_FILE) : "";

          parsedQuestions.push({
            ...normalized,
            temporaryImportId: tempId,
            importOrder: qNum,
            questionNumber: qNum,
            originalImportIndex: index,
            title: tempQ.title || `Questão Legada #${qNum}`,
            options: optImages || normalized.options,
            hasAudio: hasAudioImported,
            audioUrl: finalAudioUrl,
            associatedAudioName: hasAudioImported ? "Áudio legado importado" : "",
            status: "active",
            isValid: validation.isValid,
            errors,
            warnings,
            rawIndex: qNum
          });
        });
      }

      // 3. Post-Process Associations (such as Listening file matches)
      if (selectedCompetence === "Listening") {
        const associatedFileNames = new Set<string>();
        parsedQuestions.forEach((q, idx) => {
          let blockFieldFilename = "";
          if (!isJSON) {
            const block = blocks[idx];
            const fields = parseBlock(block);
            blockFieldFilename = (fields.AUDIO_FILE || fields.AUDIO_URL || "").trim().replace(/^.*[\\/]/, "");
          }
          
          if (blockFieldFilename) {
            const matchedFile = audioFiles.find(f => f.name.toLowerCase() === blockFieldFilename.toLowerCase());
            if (matchedFile) {
              q.associatedAudioName = matchedFile.name;
              q.associatedFile = matchedFile;
              q.audioUrl = URL.createObjectURL(matchedFile);
              q.hasAudio = true;
              associatedFileNames.add(matchedFile.name);
            }
          }
        });

        const remainingFiles = audioFiles.filter(f => !associatedFileNames.has(f.name));
        let fileIndex = 0;
        parsedQuestions.forEach(q => {
          if (!q.associatedAudioName && fileIndex < remainingFiles.length) {
            const matchedFile = remainingFiles[fileIndex];
            q.associatedAudioName = matchedFile.name;
            q.associatedFile = matchedFile;
            q.audioUrl = URL.createObjectURL(matchedFile);
            q.hasAudio = true;
            fileIndex++;
          }
        });
      }

      const initialPending: Record<string, any> = {};
      parsedQuestions.forEach(q => {
        const isLectureRapide = selectedCompetence === "Reading" && q.section === "Lecture rapide de textes et de graphiques";
        initialPending[q.temporaryImportId] = {
          supportImageFile: (isLectureRapide && selectedImageFile) ? selectedImageFile : null,
          audioFile: null,
          optionImages: { A: null, B: null, C: null, D: null }
        };
      });
      setPendingMedia(initialPending);

      // Mandatory Log A
      console.log("[CO_IMPORT_STEP_1_PARSED_ORDER]", parsedQuestions.map((q, index) => ({
        arrayIndex: index,
        temporaryImportId: q.temporaryImportId,
        importOrder: q.importOrder,
        questionNumber: q.questionNumber,
        title: q.title,
        section: q.section,
      })));

      const validQuestions = parsedQuestions.filter(q => q.isValid);
      const invalidQuestions = parsedQuestions.filter(q => !q.isValid);

      let audioWarnings = "";
      if (selectedCompetence === "Listening") {
        const qCount = parsedQuestions.length;
        const aCount = audioFiles.length;
        if (qCount > aCount) {
          audioWarnings = `Há ${qCount} questões de Listening, mas apenas ${aCount} ${aCount === 1 ? "arquivo" : "arquivos"} de áudio. As questões sem áudio serão importadas sem áudio ou você pode anexar os arquivos restantes.`;
        } else if (aCount > qCount) {
          audioWarnings = `Há ${aCount} arquivos de áudio enviados, mas apenas ${qCount} questões de Listening detectadas. Os áudios adicionais não serão importados.`;
        }
      }

      if (selectedCompetence === "Listening") {
        console.log("[IMPORT_CO_IMAGE_UPLOAD_REQUIRED]", {
          questionCount: parsedQuestions.length,
          questions: parsedQuestions.map(q => {
            const safeOptions = Array.isArray(q.options) ? q.options : normalizeImportedQuestionsPayload(q.options) || normalizeQuestionOptions(q);
            return {
              title: q.title,
              section: q.section,
              requiresOptionImages: doesQuestionRequireOptionImages(q),
              hasA: Boolean(q.optionAImage || safeOptions?.find?.((o: any) => o.label === "A")?.image || safeOptions?.find?.((o: any) => o.label === "A")?.imageUrl),
              hasB: Boolean(q.optionBImage || safeOptions?.find?.((o: any) => o.label === "B")?.image || safeOptions?.find?.((o: any) => o.label === "B")?.imageUrl),
              hasC: Boolean(q.optionCImage || safeOptions?.find?.((o: any) => o.label === "C")?.image || safeOptions?.find?.((o: any) => o.label === "C")?.imageUrl),
              hasD: Boolean(q.optionDImage || safeOptions?.find?.((o: any) => o.label === "D")?.image || safeOptions?.find?.((o: any) => o.label === "D")?.imageUrl),
            };
          }),
        });
      }

      setIsValidating(false);
      setValidationResult({
        success: validQuestions.length > 0 && generalErrors.length === 0,
        totalCount: parsedQuestions.length,
        validCount: validQuestions.length,
        invalidCount: invalidQuestions.length,
        duplicateCount: 0,
        validQuestions,
        invalidQuestions,
        allQuestions: parsedQuestions,
        uniqueCompetencies: [selectedCompetence],
        isSingleCompetency: true,
        detectedCompetency: selectedCompetence,
        audioWarnings,
        generalErrors: generalErrors.length > 0 ? generalErrors : undefined
      });

    }, 800);
  };

  const handleValidate = () => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        processText(text);
      };
      reader.readAsText(file);
    } else if (inputText) {
      processText(inputText);
    }
  };

  const handleImport = async () => {
    if (!isAdmin) {
      showLocalToast("Você não tem permissão para modificar o banco de questões.");
      return;
    }
    let uploadFailed = false;
    if (!validationResult || validationResult.allQuestions.length === 0) return;
    
    // Check constraints depending on allowPartialImport
    const hasErrors = validationResult.invalidCount > 0;
    if (hasErrors && !allowPartialImport) {
      showLocalToast("A importação foi bloqueada por conter questões com erro. Ative \"Permitir importar apenas questões válidas\" para prosseguir.");
      return;
    }

    const questionsToImport = (allowPartialImport ? validationResult.validQuestions : validationResult.allQuestions).filter(q => !q.isDuplicate);
    if (questionsToImport.length === 0) {
      showLocalToast("Nenhuma questão nova/válida disponível para importação.");
      return;
    }

    setIsImporting(true);
    
    try {
      // Enforce requiresAudio validation blocker during handleImport
      if (selectedCompetence === "Listening") {
        const missingAudioQuestions = questionsToImport.filter(q => {
          const hasExisting = isPermanentMediaUrl(q.audio || q.audioUrl);
          const hasPending = !!(pendingMedia[q.temporaryImportId]?.audioFile || resolvedAudioMappings[q.temporaryImportId]?.file);
          return !hasExisting && !hasPending;
        });

        if (missingAudioQuestions.length > 0) {
          showLocalToast(`Erro: As seguintes questões exigem áudio mas não possuem nenhum áudio associado ou permanente: ${missingAudioQuestions.map(q => `Questão ${q.importOrder}`).join(", ")}`);
          setIsImporting(false);
          return;
        }
      }

      uploadFailed = false;

      // Run all media uploads in parallel
      const uploadedMediaResults = await Promise.all(
        questionsToImport.map(async (q) => {
          const mediaForQ = pendingMedia[q.temporaryImportId];
          
          let supportImageMediaId = "";
          let supportImageUrl = "";
          let audioMediaId = "";
          let audioUrl = "";
          let optionImagesMap: Record<string, string> = {};
          let optionImagesUrlsMap: Record<string, string> = {};

          // 1. Support Image upload
          if (selectedCompetence === "Reading" && q.section === "Lecture rapide de textes et de graphiques") {
            if (mediaForQ?.supportImageFile) {
              try {
                let uploadedMedia: any = null;
                if (storageService && supabase && isSupabaseConfigured) {
                  uploadedMedia = await storageService.uploadQuestionImage(mediaForQ.supportImageFile);
                }
                const mediaId = await mediaService.saveMedia(mediaForQ.supportImageFile);
                supportImageMediaId = mediaId;
                supportImageUrl = uploadedMedia ? uploadedMedia.url : "";
              } catch (err) {
                console.error(`[Import] support image upload failed for Q-${q.importOrder}`, err);
                uploadFailed = true;
                throw new Error(`Questão ${q.importOrder}: Não foi possível enviar a imagem de suporte.`);
              }
            }
          }

          // 2. Audio upload
          if (selectedCompetence === "Listening") {
            const fileToSave = mediaForQ?.audioFile || resolvedAudioMappings[q.temporaryImportId]?.file;
            if (fileToSave) {
              try {
                console.log("[CO_MEDIA_UPLOAD_START]", {
                  temporaryImportId: q.temporaryImportId,
                  mediaKind: "audio",
                  fileName: fileToSave.name,
                  size: fileToSave.size
                });
                const publicUrl = await questionMediaUploadService.uploadQuestionMediaFile({
                  file: fileToSave,
                  mediaKind: "audio",
                  questionId: q.temporaryImportId
                });
                console.log("[CO_MEDIA_UPLOAD_PUBLIC_URLS]", {
                  temporaryImportId: q.temporaryImportId,
                  mediaKind: "audio",
                  url: publicUrl
                });
                audioMediaId = fileToSave.name;
                audioUrl = publicUrl;
              } catch (err: any) {
                console.error(`[Import] audio upload failed for Q-${q.importOrder}`, err);
                uploadFailed = true;
                throw new Error(`Questão ${q.importOrder}: Erro ao salvar áudio da questão: ${err.message || err}`);
              }
            }
          }

          // 3. Option images upload
          if (selectedCompetence === "Listening" && q.section === "Dialogues associés à des images") {
            const labels = ["A", "B", "C", "D"] as const;
            await Promise.all(
              labels.map(async (label) => {
                const optionFile = mediaForQ?.optionImages?.[label];
                if (optionFile) {
                  try {
                    console.log("[CO_MEDIA_UPLOAD_START]", {
                      temporaryImportId: q.temporaryImportId,
                      optionLabel: label,
                      mediaKind: "option-image",
                      fileName: optionFile.name,
                      size: optionFile.size
                    });
                    const publicUrl = await questionMediaUploadService.uploadQuestionMediaFile({
                      file: optionFile,
                      mediaKind: "option-image",
                      optionLabel: label,
                      questionId: q.temporaryImportId
                    });
                    console.log("[CO_MEDIA_UPLOAD_PUBLIC_URLS]", {
                      temporaryImportId: q.temporaryImportId,
                      optionLabel: label,
                      mediaKind: "option-image",
                      url: publicUrl
                    });
                    optionImagesMap[label] = optionFile.name;
                    optionImagesUrlsMap[label] = publicUrl;
                  } catch (err: any) {
                    console.error(`[Import] option image ${label} upload failed for Q-${q.importOrder}`, err);
                    uploadFailed = true;
                    throw new Error(`Questão ${q.importOrder}: Erro ao salvar a imagem da alternativa ${label}: ${err.message || err}`);
                  }
                }
              })
            );
          }

          // 4. Resolve any bare media names in q fields if they were not uploaded above
          if (selectedCompetence === "Listening") {
            const parsedAudio = q.audioUrl || (q as any).audio || (q as any).audio_url;
            if (isBareMediaName(parsedAudio) && !audioUrl) {
              const res = await resolveBareMediaNameToPublicUrl(parsedAudio, "audio");
              if (res) {
                audioUrl = res;
                audioMediaId = parsedAudio;
              }
            }

            if (q.section === "Dialogues associés à des images") {
              const labels = ["A", "B", "C", "D"] as const;
              const safeOptions = Array.isArray(q.options) ? q.options : normalizeQuestionOptions(q);
              await Promise.all(
                labels.map(async (label) => {
                  if (optionImagesUrlsMap[label]) return; // already uploaded/resolved

                  // Check if there is a bare name in option images
                  let optionImgVal = "";
                  const originalOpt = Array.isArray(safeOptions) ? safeOptions.find((o: any) => o.label === label) : null;
                  if (originalOpt?.image) {
                    optionImgVal = originalOpt.image;
                  } else if (originalOpt?.imageUrl) {
                    optionImgVal = originalOpt.imageUrl;
                  } else {
                    optionImgVal = q[`option${label}Image`] || q[`image${label}`];
                  }

                  if (isBareMediaName(optionImgVal)) {
                    const res = await resolveBareMediaNameToPublicUrl(optionImgVal, "image");
                    if (res) {
                      optionImagesUrlsMap[label] = res;
                      optionImagesMap[label] = optionImgVal;
                    }
                  }
                })
              );
            }
          }

          return {
            temporaryImportId: q.temporaryImportId,
            supportImageMediaId,
            supportImageUrl,
            audioMediaId,
            audioUrl,
            optionImagesMap,
            optionImagesUrlsMap
          };
        })
      );

      // Build lookup maps from the parallel upload results
      const mediaResultsMap = new Map(
        uploadedMediaResults.map(r => [r.temporaryImportId, r])
      );

      const questionsToSave = questionsToImport.map((q) => {
        const { isValid, errors, warnings, rawIndex, associatedFile, associatedAudioName, ...dbQuestion } = q;
        const mediaResult = mediaResultsMap.get(q.temporaryImportId);

        // Enforce stable orders
        dbQuestion.importOrder = q.importOrder;
        dbQuestion.questionNumber = q.questionNumber;
        dbQuestion.originalImportIndex = q.originalImportIndex;
        dbQuestion.temporaryImportId = q.temporaryImportId;

        // 1. Support Image mapping
        if (selectedCompetence === "Reading" && dbQuestion.section === "Lecture rapide de textes et de graphiques") {
          if (mediaResult?.supportImageMediaId) {
            dbQuestion.imageMediaId = mediaResult.supportImageMediaId;
            dbQuestion.imageUrl = mediaResult.supportImageUrl || dbQuestion.imageUrl || "";
            if (mediaResult.supportImageUrl) {
              dbQuestion.media = {
                ...dbQuestion.media,
                image: { url: mediaResult.supportImageUrl }
              };
            }
            dbQuestion.hasImage = true;
          } else if (dbQuestion.imageUrl) {
            dbQuestion.hasImage = true;
          }
        } else {
          dbQuestion.imageMediaId = undefined;
          dbQuestion.imageUrl = undefined;
          dbQuestion.hasImage = false;
        }

        // 2. Audio mapping
        if (selectedCompetence === "Listening") {
          if (mediaResult?.audioMediaId) {
            dbQuestion.audioMediaId = mediaResult.audioMediaId;
            dbQuestion.audio = mediaResult.audioUrl || mediaResult.audioMediaId;
            dbQuestion.audioUrl = mediaResult.audioUrl || mediaResult.audioMediaId;
            dbQuestion.hasAudio = true;
            if (mediaResult.audioUrl) {
              dbQuestion.media = {
                ...dbQuestion.media,
                audio: { url: mediaResult.audioUrl }
              };
              dbQuestion.metadata = {
                ...dbQuestion.metadata,
                audioUrl: mediaResult.audioUrl
              };
            }
          } else if (dbQuestion.audioUrl) {
            dbQuestion.audio = dbQuestion.audioUrl;
            dbQuestion.audioMediaId = dbQuestion.audioUrl;
            dbQuestion.hasAudio = true;
          }
        } else {
          dbQuestion.audioMediaId = undefined;
          dbQuestion.audio = undefined;
          dbQuestion.audioUrl = undefined;
          dbQuestion.hasAudio = false;
        }

        // 3. Option images mapping
        if (selectedCompetence === "Listening" && dbQuestion.section === "Dialogues associés à des images") {
          const labels = ["A", "B", "C", "D"] as const;
          const safeOptions = Array.isArray(q.options) ? q.options : normalizeImportedQuestionsPayload(q.options) || normalizeQuestionOptions(q);
          const optionImagesMap: Record<string, string> = { ...mediaResult?.optionImagesMap };
          const optionImagesUrlsMap: Record<string, string> = { ...mediaResult?.optionImagesUrlsMap };
          
          const processedOptions = labels.map((label) => {
            const uploadedOptMediaId = mediaResult?.optionImagesMap?.[label];
            const uploadedOptUrl = mediaResult?.optionImagesUrlsMap?.[label];
            let optMediaId = uploadedOptMediaId || "";
            let optImageUrl = uploadedOptUrl || "";

            if (!optMediaId && !optImageUrl) {
              const originalOpt = Array.isArray(safeOptions) ? safeOptions.find((o: any) => o.label === label) : null;
              if (originalOpt?.mediaId) {
                optMediaId = originalOpt.mediaId;
              } else if (originalOpt?.imageUrl) {
                optImageUrl = originalOpt.imageUrl;
              } else if (originalOpt?.image) {
                const imgVal = originalOpt.image;
                if (imgVal.startsWith("http") || imgVal.startsWith("data:") || imgVal.includes("/")) {
                  optImageUrl = imgVal;
                } else {
                  optMediaId = imgVal;
                }
              } else {
                const legacyImgVal = q[`option${label}Image`] || q[`image${label}`];
                if (legacyImgVal && typeof legacyImgVal === "string") {
                  if (legacyImgVal.startsWith("http") || legacyImgVal.startsWith("data:") || legacyImgVal.includes("/")) {
                    optImageUrl = legacyImgVal;
                  } else {
                    optMediaId = legacyImgVal;
                  }
                }
              }
            }

            const finalVal = optImageUrl || optMediaId || "";
            
            // Store at root level
            (dbQuestion as any)[`option${label}Image`] = finalVal;
            
            if (optMediaId) {
              optionImagesMap[label] = optMediaId;
            }
            if (optImageUrl) {
              optionImagesUrlsMap[label] = optImageUrl;
            }

            return {
              id: label,
              label,
              value: label,
              text: "",
              image: finalVal,
              imageUrl: optImageUrl || undefined,
              mediaId: optMediaId || undefined
            };
          });

          dbQuestion.options = processedOptions;
          
          dbQuestion.media = {
            ...dbQuestion.media,
            optionImages: Object.keys(optionImagesMap).length > 0 ? optionImagesMap : undefined,
            optionImagesUrls: Object.keys(optionImagesUrlsMap).length > 0 ? optionImagesUrlsMap : undefined,
          };
          
          dbQuestion.metadata = {
            ...dbQuestion.metadata,
            optionImages: Object.keys(optionImagesUrlsMap).length > 0 ? optionImagesUrlsMap : undefined,
          };
        }

        // Strict explicit competency resolution/routing
        if (selectedCompetence === "Listening") {
          dbQuestion.competency = "Listening";
          dbQuestion.competence = "Listening";
          dbQuestion.testType = "listening";
          dbQuestion.resultType = "listening";

          const finalAudioUrl = getQuestionAudioUrl(dbQuestion);
          dbQuestion.audioUrl = finalAudioUrl;
          dbQuestion.audio = finalAudioUrl;
          dbQuestion.hasAudio = !!finalAudioUrl;

          const finalImageA = getOptionImageUrl(dbQuestion, "A");
          const finalImageB = getOptionImageUrl(dbQuestion, "B");
          const finalImageC = getOptionImageUrl(dbQuestion, "C");
          const finalImageD = getOptionImageUrl(dbQuestion, "D");

          dbQuestion.optionAImage = finalImageA;
          dbQuestion.optionBImage = finalImageB;
          dbQuestion.optionCImage = finalImageC;
          dbQuestion.optionDImage = finalImageD;
          dbQuestion.hasImage = !!(finalImageA || finalImageB || finalImageC || finalImageD);

          const normalizedOptions = normalizeQuestionOptions(dbQuestion);
          dbQuestion.options = normalizedOptions;

          dbQuestion.media = {
            ...dbQuestion.media,
            audioUrl: finalAudioUrl,
            optionImages: {
              A: finalImageA,
              B: finalImageB,
              C: finalImageC,
              D: finalImageD
            }
          };

          dbQuestion.metadata = {
            ...dbQuestion.metadata,
            audioUrl: finalAudioUrl,
            optionImages: {
              A: finalImageA,
              B: finalImageB,
              C: finalImageC,
              D: finalImageD
            }
          };
        } else if (selectedCompetence === "Reading") {
          dbQuestion.competency = "Reading";
          dbQuestion.competence = "Reading";
          dbQuestion.testType = "reading";
          dbQuestion.resultType = "reading";
        } else if (selectedCompetence === "Writing") {
          dbQuestion.competency = "Writing";
          dbQuestion.competence = "Writing";
          dbQuestion.testType = "writing";
          dbQuestion.resultType = "writing_v2";
        }

        // Escape check for bare media names
        const savedAudio = dbQuestion.audioUrl;
        const savedA = dbQuestion.optionAImage;
        const savedB = dbQuestion.optionBImage;
        const savedC = dbQuestion.optionCImage;
        const savedD = dbQuestion.optionDImage;

        if (isBareMediaName(savedAudio)) {
          console.warn("[CO_IMPORT_SAVED_BARE_MEDIA_NAME_BLOCKED]", savedAudio);
        }
        if (isBareMediaName(savedA)) {
          console.warn("[CO_IMPORT_SAVED_BARE_MEDIA_NAME_BLOCKED]", savedA);
        }
        if (isBareMediaName(savedB)) {
          console.warn("[CO_IMPORT_SAVED_BARE_MEDIA_NAME_BLOCKED]", savedB);
        }
        if (isBareMediaName(savedC)) {
          console.warn("[CO_IMPORT_SAVED_BARE_MEDIA_NAME_BLOCKED]", savedC);
        }
        if (isBareMediaName(savedD)) {
          console.warn("[CO_IMPORT_SAVED_BARE_MEDIA_NAME_BLOCKED]", savedD);
        }

        return dbQuestion;
      });

      // Mandatory Log C
      console.log("[CO_IMPORT_STEP_3_FINAL_PAYLOAD_ORDER]", questionsToSave.map(q => ({
        temporaryImportId: q.temporaryImportId,
        importOrder: q.importOrder,
        questionNumber: q.questionNumber,
        title: q.title,
        audio: q.audio,
        audioUrl: q.audioUrl,
        competency: q.competency,
      })));

      const result = await questionService.importQuestions(questionsToSave as any);
      
      let successMsg = "";
      if (result && result.status === "success") {
        successMsg = `Importação concluída! Questões inseridas: ${result.insertedCount}.`;
        if (result.skippedDuplicateCount > 0) {
          successMsg += ` Questões duplicadas ignoradas: ${result.skippedDuplicateCount}.`;
        }
      } else {
        successMsg = allowPartialImport && hasErrors 
          ? `${questionsToSave.length} questões importadas com sucesso. ${validationResult.invalidCount} questões ignoradas por erro.`
          : `${questionsToSave.length} questões importadas com sucesso.`;
      }

      showLocalToast(successMsg);

      // Clear draft on successful import
      clearImportDraft(userId, "import_success");

      // Refetch database questions to sync duplicate checking database cache
      try {
        const questions = await questionService.getQuestions();
        setExistingQuestions(questions || []);
      } catch (err) {
        console.error("Erro ao recarregar questões após importação:", err);
      }

      resetImportPage();
    } catch (error: any) {
      console.error("Error saving questions:", error);
      if (uploadFailed) {
        showLocalToast("Não foi possível salvar a questão porque uma ou mais mídias não foram enviadas corretamente. Tente anexar os arquivos novamente.");
      } else {
        showLocalToast(error.message || "Erro ao salvar questões com mídia.");
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleClear = () => {
    setInputText("");
    setFile(null);
    setAudioFiles([]);
    setBatchImageFiles([]);
    setManualAudioMappings({});
    setResolvedAudioMappings({});
    setPendingMedia({});
    setValidationResult(null);
    setExpandedIndex(null);
    setSelectedImageFile(null);
    setImagePreviewUrl("");
  };

  const handleCopySingleTemplate = (tpl: ImportTemplate) => {
    navigator.clipboard.writeText(JSON.stringify(tpl.template, null, 2));
    showLocalToast(t("importer.templateCopied", { name: t(`importer.templates.${tpl.id}.label`, tpl.label) }));
  };

  const handleDownloadSingleTemplate = (tpl: ImportTemplate) => {
    const content = JSON.stringify(tpl.template, null, 2);
    const filename = tpl.filename;
    
    const element = document.createElement("a");
    const fileBlob = new Blob([content], { type: 'application/json;charset=utf-8' });
    element.href = URL.createObjectURL(fileBlob);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    showLocalToast(t("importer.downloadStarted", { name: filename }));
  };

  // Live reactive media validation for CO/Listening
  const allAreDuplicates = validationResult && 
    validationResult.allQuestions.length > 0 && 
    validationResult.allQuestions.every(q => q.isDuplicate);

  let activeAudioWarning = "";
  let activeImageWarning = "";

  if (selectedCompetence === "Listening" && validationResult?.allQuestions) {
    const questions = validationResult.allQuestions;

    const questionsMissingAudio = questions.filter(q => {
      const mediaForQ = pendingMedia[q.temporaryImportId];
      const hasPendingAudio = mediaForQ?.audioFile !== null && mediaForQ?.audioFile !== undefined;
      const mappedAudio = resolvedAudioMappings[q.temporaryImportId]?.file;
      const hasMappedAudio = !!mappedAudio;
      const existingAudioUrl = q.audioUrl || q.audio || (q as any).audio_url;
      const hasExistingAudio = isPermanentMediaUrl(existingAudioUrl);
      return !hasPendingAudio && !hasMappedAudio && !hasExistingAudio;
    });

    if (questionsMissingAudio.length > 0) {
      activeAudioWarning = `Há ${questionsMissingAudio.length} questão(ões) de Listening sem áudio associado. Anexe ou cole o áudio antes de gravar.`;
    }

    const questionsRequiringOptionImages = questions.filter(doesQuestionRequireOptionImages);
    const questionsMissingOptionImages = questionsRequiringOptionImages.filter(q => {
      const mediaForQ = pendingMedia[q.temporaryImportId];
      const labels = ["A", "B", "C", "D"] as const;
      return labels.some(label => {
        const hasPendingImage = mediaForQ?.optionImages?.[label] !== null && mediaForQ?.optionImages?.[label] !== undefined;
        const safeOptions = Array.isArray(q.options) ? q.options : normalizeImportedQuestionsPayload(q.options) || normalizeQuestionOptions(q);
        const optObj = Array.isArray(safeOptions) ? safeOptions.find((o: any) => o.label === label) : null;
        const existingUrl = optObj?.imageUrl || optObj?.mediaId || optObj?.image || q[`option${label}Image`] || q[`image${label}`];
        const hasExistingUrl = isPermanentMediaUrl(existingUrl);
        return !hasPendingImage && !hasExistingUrl;
      });
    });

    if (questionsMissingOptionImages.length > 0) {
      activeImageWarning = `Há ${questionsMissingOptionImages.length} questão(ões) do tipo 'Dialogues associés à des images' com uma ou mais imagens de alternativas ausentes.`;
    }

    console.log("[IMPORT_QUALITY_MEDIA_STATUS]", {
      totalQuestions: questions.length,
      listeningQuestions: questions.map(q => {
        const mediaForQ = pendingMedia[q.temporaryImportId];
        const hasPendingAudio = mediaForQ?.audioFile !== null && mediaForQ?.audioFile !== undefined;
        const mappedAudio = resolvedAudioMappings[q.temporaryImportId]?.file;
        const hasMappedAudio = !!mappedAudio;
        const existingAudioUrl = q.audioUrl || q.audio || (q as any).audio_url;
        const hasExistingAudio = isPermanentMediaUrl(existingAudioUrl);
        return {
          id: q.id,
          temporaryImportId: q.temporaryImportId,
          title: q.title,
          requiresAudio: true,
          hasAudio: hasPendingAudio || hasMappedAudio || hasExistingAudio,
          audioUrl: q.audioUrl,
          audio: q.audio,
          hasPendingAudioFile: hasPendingAudio || hasMappedAudio,
        };
      }),
      missingAudioCount: questionsMissingAudio.length,
    });

    console.log("[IMPORT_QUALITY_IMAGE_STATUS]", {
      imageQuestions: questionsRequiringOptionImages.map(q => {
        const hasImageForOption = (label: "A" | "B" | "C" | "D") => {
          const mediaForQ = pendingMedia[q.temporaryImportId];
          const hasPendingImage = mediaForQ?.optionImages?.[label] !== null && mediaForQ?.optionImages?.[label] !== undefined;
          const safeOptions = Array.isArray(q.options) ? q.options : normalizeImportedQuestionsPayload(q.options) || normalizeQuestionOptions(q);
          const optObj = Array.isArray(safeOptions) ? safeOptions.find((o: any) => o.label === label) : null;
          const existingUrl = optObj?.imageUrl || optObj?.mediaId || optObj?.image || q[`option${label}Image`] || q[`image${label}`];
          const hasExistingUrl = isPermanentMediaUrl(existingUrl);
          return hasPendingImage || hasExistingUrl;
        };
        return {
          id: q.id,
          title: q.title,
          A: hasImageForOption("A"),
          B: hasImageForOption("B"),
          C: hasImageForOption("C"),
          D: hasImageForOption("D"),
        };
      }),
    });
  }

  const steps = [
    { number: 1, label: t("importer.competence", "Competência"), desc: t("importer.ceCoOrEe", "CE, CO ou EE") },
    { number: 2, label: t("importer.subType", "Sub-tipo de Questão"), desc: t("importer.specificationAndModel", "Especificação e modelo") },
    { number: 3, label: t("importer.content", "Conteúdo"), desc: t("importer.jsonOrMarkdown", "JSON ou Marcadores") },
    { number: 4, label: t("importer.qualityAndMedia", "Qualidade & Mídias"), desc: t("importer.reviewAndSave", "Revisão e gravação") }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-6xl mx-auto">
      
      {/* LOCAL TOAST NOTIFICATION CONTAINER */}
      {localToast && (
        <div className="fixed top-6 right-6 z-[100] bg-gray-950 text-white font-semibold text-xs py-3 px-5 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-top-4 duration-300">
          <div className="w-2 h-2 rounded-full bg-primary animate-ping" />
          <span>{localToast}</span>
        </div>
      )}

      {/* DRAFT RESTORE PROMPT */}
      {showRestorePrompt && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-5 text-white">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                  <FileText className="w-4 h-4" />
                </div>
                <span className="font-black text-base">{t("importer.draftFound")}</span>
              </div>
              <p className="text-blue-100 text-xs leading-relaxed">
                {t("importer.draftRestorePrompt")}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <button
                id="restore-draft-btn"
                onClick={handleRestoreDraft}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-sm flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                {t("importer.continueDraft")}
              </button>
              <button
                id="discard-draft-btn"
                onClick={handleDiscardDraft}
                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {t("importer.discardDraft")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DRAFT STATUS INDICATOR */}
      {draftStatus === "saving" && (
        <div className="fixed bottom-4 right-4 z-[90] bg-white border border-gray-100 shadow-lg rounded-xl py-2 px-3 flex items-center gap-2 text-[11px] text-gray-500 font-semibold animate-in fade-in duration-200">
          <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
          {t("importer.savingDraft")}
        </div>
      )}
      {draftStatus === "restored" && (
        <div className="fixed bottom-4 right-4 z-[90] bg-emerald-50 border border-emerald-200 shadow-lg rounded-xl py-2 px-3 flex items-center gap-2 text-[11px] text-emerald-700 font-semibold animate-in fade-in duration-200">
          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
          {t("importer.draftRestored")}
        </div>
      )}
      {draftStatus === "save_failed" && (
        <div className="fixed bottom-4 right-4 z-[90] bg-red-50 border border-red-200 shadow-lg rounded-xl py-2 px-3 flex items-center gap-2 text-[11px] text-red-700 font-semibold animate-in fade-in duration-200">
          <AlertCircle className="w-3 h-3 text-red-500" />
          {t("importer.draftSaveFailed")}
        </div>
      )}

      {/* Header back button */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (currentStep > 1) {
              setCurrentStep(prev => prev - 1);
            } else {
              navigate(-1);
            }
          }}
          className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 rounded-lg gap-1.5 font-semibold text-xs border border-gray-200/40 bg-white"
        >
          <ArrowLeft className="w-4 h-4" />
          {currentStep > 1 ? t("importer.backToStep", "Voltar ao Passo {{step}}", { step: currentStep - 1 }) : t("importer.backToQuestions", "Voltar para Questões")}
        </Button>
        
        {/* Step indicator pill on the right */}
        <Badge variant="outline" className="bg-slate-50 text-slate-500 font-bold border-slate-100 py-1 px-3 rounded-lg text-[10px]">
          {t("importer.stepOf", "Passo {{current}} de {{total}}", { current: currentStep, total: 4 })}
        </Badge>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-gray-900">{t("importer.title", "Importador de Questões")}</h1>
          <p className="text-gray-500 mt-1.5 text-sm">{t("importer.subtitle", "Painel dinâmico e guiado por etapas para importação de novas questões de forma qualificada.")}</p>
        </div>
      </div>

      {/* STEPPER PROGRESS HEADERS */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-xs">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative">
          {steps.map((st) => {
            const isActive = currentStep === st.number;
            const isCompleted = currentStep > st.number;
            return (
              <div 
                key={st.number} 
                className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                  isActive 
                    ? "bg-purple-50/60 border border-purple-100/60" 
                    : isCompleted 
                      ? "bg-emerald-50/10 border border-emerald-100/20" 
                      : "border border-transparent"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 shadow-xs transition-all ${
                  isActive 
                    ? "bg-purple-600 text-white" 
                    : isCompleted 
                      ? "bg-emerald-500 text-white" 
                      : "bg-slate-100 text-slate-400"
                }`}>
                  {isCompleted ? <Check className="w-4 h-4 stroke-[3]" /> : st.number}
                </div>
                <div className="min-w-0">
                  <span className={`text-xs font-bold block ${isActive ? "text-purple-900" : isCompleted ? "text-emerald-950" : "text-slate-500"}`}>
                    {st.label}
                  </span>
                  <span className="text-[10px] text-slate-400 block truncate font-medium">
                    {st.desc}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* STEP 1: SELECT GENERAL COMPETENCE */}
      {currentStep === 1 && (
        <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl animate-in fade-in duration-300">
          <CardHeader className="pb-3 bg-gray-50/50 border-b border-gray-100">
            <CardTitle className="text-base font-bold text-gray-800">{t("importer.ceCoOrEe")}</CardTitle>
            <CardDescription className="text-xs">O validador interno e as regras de mídias se adaptam perfeitamente para garantir qualidade pedagógica.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="grid gap-4 sm:grid-cols-3">
              {/* Reading Card */}
              <div 
                onClick={() => {
                  setSelectedCompetence("Reading");
                  setValidationResult(null);
                  setExpandedIndex(null);
                }}
                className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 relative overflow-hidden group ${
                  selectedCompetence === "Reading"
                    ? "border-purple-500 bg-purple-50/40 shadow-md scale-[1.01]"
                    : "border-gray-100 bg-white hover:border-purple-200 hover:shadow-sm"
                }`}
              >
                <div className="flex flex-col items-start gap-3">
                  <div className={`p-3 rounded-xl transition-all ${
                    selectedCompetence === "Reading"
                      ? "bg-purple-500 text-white shadow-sm"
                      : "bg-purple-50 text-purple-600 group-hover:bg-purple-100"
                  }`}>
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">Compréhension Écrite (CE)</h4>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">JSON de leitura ou legado. Valida as seções oficiais do módulo de leitura.</p>
                  </div>
                </div>
                {selectedCompetence === "Reading" && (
                  <div className="absolute top-3.5 right-3.5 p-1 bg-purple-500 text-white rounded-full shadow-sm">
                    <Check className="w-3.5 h-3.5 stroke-[3]" />
                  </div>
                )}
              </div>

              {/* Listening Card */}
              <div 
                onClick={() => {
                  setSelectedCompetence("Listening");
                  setValidationResult(null);
                  setExpandedIndex(null);
                }}
                className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 relative overflow-hidden group ${
                  selectedCompetence === "Listening"
                    ? "border-blue-500 bg-blue-50/40 shadow-md scale-[1.01]"
                    : "border-gray-100 bg-white hover:border-blue-200 hover:shadow-sm"
                }`}
              >
                <div className="flex flex-col items-start gap-3">
                  <div className={`p-3 rounded-xl transition-all ${
                    selectedCompetence === "Listening"
                      ? "bg-blue-500 text-white shadow-sm"
                      : "bg-blue-50 text-blue-600 group-hover:bg-blue-100"
                  }`}>
                    <Headphones className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">Compréhension Orale (CO)</h4>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{t("startTest.listeningDesc")}</p>
                  </div>
                </div>
                {selectedCompetence === "Listening" && (
                  <div className="absolute top-3.5 right-3.5 p-1 bg-blue-500 text-white rounded-full shadow-sm">
                    <Check className="w-3.5 h-3.5 stroke-[3]" />
                  </div>
                )}
              </div>

              {/* Writing Card */}
              <div 
                onClick={() => {
                  setSelectedCompetence("Writing");
                  setValidationResult(null);
                  setExpandedIndex(null);
                }}
                className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 relative overflow-hidden group ${
                  selectedCompetence === "Writing"
                    ? "border-emerald-500 bg-emerald-50/40 shadow-md scale-[1.01]"
                    : "border-gray-100 bg-white hover:border-emerald-200 hover:shadow-sm"
                }`}
              >
                <div className="flex flex-col items-start gap-3">
                  <div className={`p-3 rounded-xl transition-all ${
                    selectedCompetence === "Writing"
                      ? "bg-emerald-500 text-white shadow-sm"
                      : "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100"
                  }`}>
                    <PenTool className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">Expression Écrite (EE)</h4>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{t("startTest.writingDesc")}</p>
                  </div>
                </div>
                {selectedCompetence === "Writing" && (
                  <div className="absolute top-3.5 right-3.5 p-1 bg-emerald-500 text-white rounded-full shadow-sm">
                    <Check className="w-3.5 h-3.5 stroke-[3]" />
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end pt-4 border-t border-slate-100">
              <Button
                disabled={!selectedCompetence}
                onClick={() => setCurrentStep(2)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-xs px-6 h-10 flex items-center gap-1.5 shadow-xs"
              >
                {t("importer.nextToSubtype", "Avançar para Sub-tipo")}
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: SELECT SUBTYPE & SHOW DESCRIPTION / MODEL */}
      {currentStep === 2 && (
        <div className="grid gap-6 md:grid-cols-3 animate-in fade-in duration-300">
          {/* Subtype selector cards list on the left (1 col) */}
          <div className="md:col-span-1 space-y-3">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">{t("importer.specificationAndModel")}</span>
            {(() => {
              const templates = 
                selectedCompetence === "Reading" ? CE_IMPORT_TEMPLATES :
                selectedCompetence === "Listening" ? CO_IMPORT_TEMPLATES :
                EE_IMPORT_TEMPLATES;
              
              return templates.map((tpl) => {
                const isSelected = selectedTemplateId === tpl.id;
                return (
                  <div
                    key={tpl.id}
                    onClick={() => {
                      setSelectedTemplateId(tpl.id);
                      setIsManualSubtypeSelected(false);
                      if (tpl.id === "ce_phrases_textes_lacunaires") {
                        setImportSubSection("phrases_lacunaires");
                      } else if (tpl.id === "ce_lecture_rapide_textes_graphiques") {
                        setImportSubSection("lecture_rapide_textes");
                      }
                    }}
                    className={`p-4 rounded-xl border cursor-pointer transition-all duration-150 relative ${
                      isSelected 
                        ? "border-purple-500 bg-purple-50/30 shadow-xs scale-[1.01]"
                        : "border-gray-100 bg-white hover:border-purple-200"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg shrink-0 mt-0.5 transition-all ${
                        isSelected ? "bg-purple-500 text-white shadow-xs" : "bg-slate-50 text-slate-400"
                      }`}>
                        {selectedCompetence === "Reading" && <BookOpen className="w-4 h-4" />}
                        {selectedCompetence === "Listening" && <Headphones className="w-4 h-4" />}
                        {selectedCompetence === "Writing" && <PenTool className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="font-bold text-gray-800 text-xs block truncate leading-tight">
                          {t(`importer.templates.${tpl.id}.label`, tpl.label)}
                        </span>
                        <p className="text-[10px] text-gray-400 mt-1 leading-normal line-clamp-2">
                          {t(`importer.templates.${tpl.id}.description`, tpl.description)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Subtype template detail on the right (2 cols) */}
          <div className="md:col-span-2 space-y-4">
            {(() => {
              const templates = 
                selectedCompetence === "Reading" ? CE_IMPORT_TEMPLATES :
                selectedCompetence === "Listening" ? CO_IMPORT_TEMPLATES :
                EE_IMPORT_TEMPLATES;
              
              const activeTpl = templates.find(t => t.id === selectedTemplateId) || templates[0];
              if (!activeTpl) return null;

              return (
                <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl h-full flex flex-col justify-between">
                  <div>
                    <CardHeader className="pb-3 border-b border-gray-50 bg-gray-50/30">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <CardTitle className="text-sm font-bold text-slate-800">
                          {t(`importer.templates.${activeTpl.id}.label`, activeTpl.label)}
                        </CardTitle>
                        <div className="flex gap-1.5">
                          {activeTpl.requiresSupportImage && (
                            <Badge className="bg-purple-100 text-purple-800 border-none text-[8px] font-bold px-2 py-0.5 rounded-lg">{t("newQuestion.illustrativeImage")}</Badge>
                          )}
                          {activeTpl.requiresAudio && (
                            <Badge className="bg-blue-100 text-blue-800 border-none text-[8px] font-bold px-2 py-0.5 rounded-lg">{t("newQuestion.audioFile")}</Badge>
                          )}
                          {activeTpl.requiresOptionImages && (
                            <Badge className="bg-indigo-100 text-indigo-800 border-none text-[8px] font-bold px-2 py-0.5 rounded-lg">{t("importer.coDialoguesImagesBadge")}</Badge>
                          )}
                        </div>
                      </div>
                      <CardDescription className="text-xs">
                        {t(`importer.templates.${activeTpl.id}.description`, activeTpl.description)}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="pt-4 space-y-3 min-w-0">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("importer.jsonOrMarkdown")}</span>
                      <div className="w-full max-w-full overflow-x-auto rounded-xl bg-slate-900 border border-slate-800">
                        <pre className="text-purple-200 text-[10px] font-mono p-4 max-h-[280px] whitespace-pre-wrap break-words leading-relaxed min-w-0">
                          {JSON.stringify(activeTpl.template, null, 2)}
                        </pre>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-2.5 pt-2 w-full">
                        <Button
                          variant="outline"
                          onClick={() => handleCopySingleTemplate(activeTpl)}
                          className="w-full sm:flex-1 rounded-xl h-9 border-gray-200 text-gray-700 font-bold text-xs bg-white hover:bg-slate-50 flex items-center justify-center gap-1.5"
                        >
                          <Copy className="w-3.5 h-3.5 text-gray-400" />
                          {t("importer.copyModelJson", "Copiar JSON do Modelo")}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleDownloadSingleTemplate(activeTpl)}
                          className="w-full sm:flex-1 rounded-xl h-9 border-gray-200 text-gray-700 font-bold text-xs bg-white hover:bg-slate-50 flex items-center justify-center gap-1.5"
                        >
                          <Download className="w-3.5 h-3.5 text-gray-400" />
                          {t("importer.downloadJson", "Baixar Arquivo JSON")}
                        </Button>
                      </div>
                    </CardContent>
                  </div>
                  
                  <CardFooter className="border-t border-slate-100 bg-slate-50/50 p-4 flex flex-col sm:flex-row justify-between gap-3 w-full">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(1)}
                      className="w-full sm:w-auto rounded-xl h-10 border-gray-200 font-bold text-xs text-gray-600 bg-white hover:bg-gray-50"
                    >
                      {t("importer.backToStep1", "Voltar ao Passo 1")}
                    </Button>
                    <Button
                      onClick={() => setCurrentStep(3)}
                      className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-xs px-6 h-10 flex items-center justify-center gap-1.5 shadow-xs"
                    >
                      {t("importer.nextToSubmitContent")}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </CardFooter>
                </Card>
              );
            })()}
          </div>
        </div>
      )}

      {/* STEP 3: SUBMIT CONTENT FOR VALIDATION */}
      {currentStep === 3 && (
        <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl animate-in fade-in duration-300">
          <CardHeader className="pb-3 bg-gray-50/50 border-b border-gray-100">
            <CardTitle className="text-base font-bold text-gray-800">{t("importer.submitContentTitle")}</CardTitle>
            <CardDescription className="text-xs">
              {t("importer.submitContentDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            
            {/* Input tabs */}
            <Tabs defaultValue="file" className="w-full">
              <TabsList className="bg-gray-100 p-1 rounded-xl w-full flex flex-col sm:flex-row h-auto gap-1">
                <TabsTrigger value="file" className="w-full justify-center text-center whitespace-normal rounded-lg text-xs font-bold py-2">{t("importer.jsonOrMarkdown")}</TabsTrigger>
                <TabsTrigger value="text" className="w-full justify-center text-center whitespace-normal rounded-lg text-xs font-bold py-2">{t("importer.changeContent")}</TabsTrigger>
              </TabsList>

              <TabsContent value="file" className="mt-4">
                <div
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all duration-200 cursor-pointer ${
                    dragActive 
                      ? "border-purple-400 bg-purple-50/10 scale-[0.99]" 
                      : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-3.5 bg-purple-50 text-purple-600 rounded-full">
                      <Upload className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm">
                        {file ? file.name : "Arraste e solte o arquivo de questões aqui"}
                      </h3>
                      <p className="text-xs text-gray-400 mt-1 font-medium">
                        {file ? `${(file.size / 1024).toFixed(2)} KB` : "Suporta arquivos JSON oficiais ou arquivos de texto legado (.txt, .md)"}
                      </p>
                    </div>
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      accept=".json,.txt,.md"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                    <Button asChild variant="outline" size="sm" className="rounded-xl mt-1.5 bg-white font-bold border-gray-200 text-xs text-gray-700 shadow-sm hover:bg-gray-50">
                      <label htmlFor="file-upload" className="cursor-pointer">
                        Selecionar Arquivo
                      </label>
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="text" className="mt-4">
                <div className="space-y-3">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide block">{t("importer.content")}</span>
                  <Textarea
                    placeholder={`{
  "competency": "${selectedCompetence?.toLowerCase()}",
  "questions": [
    // Cole aqui as questões seguindo o padrão recomendado...
  ]
}`}
                    className="min-h-[220px] font-mono text-xs bg-gray-50 border-none rounded-xl focus-visible:ring-1 focus-visible:ring-primary/20 resize-y p-4"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                  />
                </div>
              </TabsContent>
            </Tabs>

            {/* Optional Subtype & Image Uploader for CE */}
            {selectedCompetence === "Reading" && (selectedTemplateId === "ce_lecture_rapide_textes_graphiques" || selectedTemplateId === "ce_phrases_textes_lacunaires") && (
              <div className="p-5 bg-purple-50/40 border border-purple-100 rounded-2xl space-y-4 animate-in fade-in duration-300">
                <div className="space-y-3">
                  <Label className="text-sm font-black text-purple-950 block">{t("importer.subType")}</Label>
                  <p className="text-xs text-purple-800 leading-relaxed font-medium">
                    {selectedTemplateId === "ce_phrases_textes_lacunaires" 
                      ? "Esta seção é dividida em Phrases lacunaires e Textes lacunaires no teste completo. Escolha o subtipo para classificar corretamente a questão importada."
                      : "Esta seção é dividida em Lecture rapide de textes e Lecture rapide de graphiques no teste completo. Escolha o subtipo para classificar corretamente a questão importada."}
                  </p>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 pt-1">
                    {selectedTemplateId === "ce_phrases_textes_lacunaires" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setImportSubSection("phrases_lacunaires");
                            setIsManualSubtypeSelected(true);
                          }}
                          className={`p-4 rounded-xl border-2 text-left transition-all flex flex-col justify-center min-h-[3.5rem] whitespace-normal break-words ${
                            importSubSection === "phrases_lacunaires"
                              ? "border-purple-600 bg-purple-100/80 text-purple-950 font-bold shadow-xs scale-[1.01]"
                              : "border-purple-200 bg-white hover:border-purple-400 text-gray-700 font-semibold"
                          }`}
                        >
                          <span className="text-xs">Phrases lacunaires</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setImportSubSection("textes_lacunaires");
                            setIsManualSubtypeSelected(true);
                          }}
                          className={`p-4 rounded-xl border-2 text-left transition-all flex flex-col justify-center min-h-[3.5rem] whitespace-normal break-words ${
                            importSubSection === "textes_lacunaires"
                              ? "border-purple-600 bg-purple-100/80 text-purple-950 font-bold shadow-xs scale-[1.01]"
                              : "border-purple-200 bg-white hover:border-purple-400 text-gray-700 font-semibold"
                          }`}
                        >
                          <span className="text-xs">Textes lacunaires</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setImportSubSection("lecture_rapide_textes");
                            setIsManualSubtypeSelected(true);
                          }}
                          className={`p-4 rounded-xl border-2 text-left transition-all flex flex-col justify-center min-h-[3.5rem] whitespace-normal break-words ${
                            importSubSection === "lecture_rapide_textes"
                              ? "border-purple-600 bg-purple-100/80 text-purple-950 font-bold shadow-xs scale-[1.01]"
                              : "border-purple-200 bg-white hover:border-purple-400 text-gray-700 font-semibold"
                          }`}
                        >
                          <span className="text-xs">Lecture rapide de textes</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setImportSubSection("lecture_rapide_graphiques");
                            setIsManualSubtypeSelected(true);
                          }}
                          className={`p-4 rounded-xl border-2 text-left transition-all flex flex-col justify-center min-h-[3.5rem] whitespace-normal break-words ${
                            importSubSection === "lecture_rapide_graphiques"
                              ? "border-purple-600 bg-purple-100/80 text-purple-950 font-bold shadow-xs scale-[1.01]"
                              : "border-purple-200 bg-white hover:border-purple-400 text-gray-700 font-semibold"
                          }`}
                        >
                          <span className="text-xs">Lecture rapide de graphiques</span>
                        </button>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 pt-2 text-[11px] font-bold text-purple-900">
                    <AlertCircle className="w-4 h-4 text-purple-600 shrink-0" />
                    <span>O subtipo selecionado será aplicado a todas as questões deste lote.</span>
                  </div>
                </div>

                {selectedTemplateId === "ce_lecture_rapide_textes_graphiques" && importSubSection === "lecture_rapide_graphiques" && (
                  <div className="pt-4 border-t border-purple-100/80 space-y-3">
                    <div>
                      <span className="text-xs font-bold text-purple-950 block flex items-center gap-1.5">
                        <Image className="w-4 h-4 text-purple-600" />
                        Imagem/gráfico de suporte (opcional)
                      </span>
                      <p className="text-[10px] text-purple-700/80 mt-0.5 leading-relaxed">
                        Você pode anexar opcionalmente uma imagem ou gráfico de suporte para a questão.
                      </p>
                    </div>
                    <ImageUploaderWidget
                      label="Imagem de Suporte Opcional"
                      file={selectedImageFile}
                      existingUrl={imagePreviewUrl}
                      onChange={(file) => {
                        setSelectedImageFile(file);
                        if (file) {
                          const url = URL.createObjectURL(file);
                          setImagePreviewUrl(url);
                        } else {
                          setImagePreviewUrl("");
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Parameter configuration switches */}
            <div className="bg-slate-50/50 border border-slate-100 p-4 rounded-xl space-y-3.5">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">{t("importer.qualityPanel")}</span>
              
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold text-gray-800 text-xs">{t("importer.integrityConfirmation")}</h4>
                  <p className="text-[10px] text-gray-500 mt-0.5">{t("importer.integrityConfirmationText", "If active, only healthy questions are saved and defective ones are ignored.")}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input 
                    type="checkbox" 
                    checked={allowPartialImport} 
                    onChange={(e) => setAllowPartialImport(e.target.checked)}
                    className="sr-only peer" 
                  />
                  <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              {selectedCompetence !== "Writing" && (
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-gray-800 text-xs">{t("importer.hideAnswersSettings", "Blind review mode")}</h4>
                    <p className="text-[10px] text-gray-500 mt-0.5">{t("importer.hideAnswersSettings")}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input 
                      type="checkbox" 
                      checked={blindMode} 
                      onChange={(e) => setBlindMode(e.target.checked)}
                      className="sr-only peer" 
                    />
                    <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
              )}
            </div>

            {/* Buttons row */}
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(2)}
                className="w-full sm:w-auto rounded-xl h-12 border-gray-200 text-gray-700 font-bold text-xs bg-white hover:bg-slate-50"
              >
                Voltar ao Passo 2
              </Button>
              <Button
                disabled={isValidating || (!inputText && !file)}
                onClick={handleValidate}
                className="w-full sm:flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-xs min-h-[3rem] h-auto py-3 flex items-center justify-center gap-2 whitespace-normal break-words text-center shadow-md"
              >
                {isValidating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white shrink-0" />
                    <span>{t("common.analyzing", "Analyzing and validating content...")}</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-purple-200 shrink-0" />
                    <span>{t("importer.reviewAndSave")}</span>
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 4: QUALITY BOARD, FILE ATTACHMENTS & DB IMPORT */}
      {currentStep === 4 && (
        <div className="grid gap-6 lg:grid-cols-3 animate-in fade-in duration-300">
          <div className="lg:col-span-3">
            <div className="p-4 bg-amber-50 text-amber-900 border border-amber-100 rounded-2xl flex items-start gap-3 shadow-sm">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-xs space-y-1">
                <span className="font-bold text-amber-950 block">{t("importer.oldImportedQuestionsWarning", "Warning about old imported questions")}</span>
                <p className="font-medium leading-relaxed">
                  Caso questões importadas antigamente (antes desta correção) continuem com áudios ou ordem invertidos, será necessário <strong>excluí-las do banco de dados e reimportá-las</strong> usando este importador corrigido. A nova versão garante a integridade estável da ordem e das mídias.
                </p>
              </div>
            </div>
          </div>
          
          {/* Main setup & upload zone on the left */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Listening bulk media uploader */}
            {selectedCompetence === "Listening" && (
              <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl">
                <CardHeader className="pb-3 bg-gray-50/50 border-b border-gray-100">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-blue-500" />
                    Anexar Arquivos de Áudio (Upload em Lote)
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Arraste vários áudios de simulado (.mp3, .wav) simultaneamente. O importador mapeará automaticamente para cada questão pelo nome do arquivo.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="border-2 border-dashed border-blue-100 rounded-xl p-6 bg-blue-50/10 text-center hover:bg-blue-50/20 transition-all relative">
                    <Music className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-700">{t("importer.clickToSelect")}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{t("importer.dragDropAudioFormats")}</p>
                    <input
                      type="file"
                      multiple
                      accept=".mp3,.wav,.m4a"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) => {
                        if (e.target.files) {
                          const newFiles = Array.from(e.target.files);
                          setAudioFiles(prev => {
                            const existingNames = new Set(prev.map(f => f.name));
                            const filteredNew = newFiles.filter(f => !existingNames.has(f.name));
                            return [...prev, ...filteredNew];
                          });
                        }
                      }}
                    />
                  </div>

                  {audioFiles.length > 0 && (
                    <div className="space-y-1.5 animate-in fade-in">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Áudios adicionados ({audioFiles.length}):</span>
                      <div className="max-h-[160px] overflow-y-auto space-y-1.5 pr-1">
                        {audioFiles.map((f, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl text-xs border border-gray-100/50 hover:bg-gray-100/10 transition-all">
                            <div className="flex items-center gap-2 min-w-0">
                              <Music className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                              <span className="font-semibold text-gray-700 truncate max-w-[200px]">{f.name}</span>
                              <span className="text-gray-400 text-[10px]">({(f.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            <button
                              onClick={() => setAudioFiles(prev => prev.filter((_, i) => i !== idx))}
                              className="p-1 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Ctrl+V Paste Area for Audio */}
                  {validationResult?.allQuestions && validationResult.allQuestions.length > 0 && (
                    <div className="pt-4 border-t border-gray-100 space-y-3 animate-in fade-in">
                      <div className="flex flex-col gap-1.5">
                        <Label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                          Questão Destino para Colar Áudio
                        </Label>
                        <Select
                          value={pastedAudioTargetQId}
                          onValueChange={setPastedAudioTargetQId}
                        >
                          <SelectTrigger className="w-full h-9 bg-gray-50/50 hover:bg-gray-50 border-gray-200 text-xs font-semibold rounded-xl focus:ring-purple-500/20">
                            <SelectValue placeholder="Selecione a questão alvo" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl border-gray-200">
                            {validationResult.allQuestions.map((q) => (
                              <SelectItem
                                key={q.temporaryImportId}
                                value={q.temporaryImportId}
                                className="text-xs font-medium rounded-lg"
                              >
                                Questão {q.importOrder || q.questionNumber || q.rawIndex}: {q.title || "Sem título"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <ClipboardMediaPasteBox
                        mediaType="audio"
                        label="Colar Áudio ou URL via Ctrl+V"
                        description="Copie um arquivo de áudio (Ctrl+C) ou uma URL (.mp3, .wav) e cole aqui"
                        acceptedMimeTypes={["audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav", "audio/m4a", "audio/x-m4a", "audio/ogg"]}
                        acceptedExtensions={[".mp3", ".wav", ".m4a", ".ogg"]}
                        onFilePaste={(file) => handleAudioFilePaste(file, pastedAudioTargetQId)}
                        onUrlPaste={(url) => handleAudioUrlPaste(url, pastedAudioTargetQId)}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Mapeamento dos Áudios das Questões Panel */}
            {selectedCompetence === "Listening" && validationResult?.allQuestions && (
              <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl animate-in fade-in duration-300">
                <CardHeader className="pb-3 bg-gray-50/50 border-b border-gray-100">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-blue-500" />
                    Mapeamento dos Áudios das Questões
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Verifique se os áudios foram associados às questões corretas. Você pode trocar ou remover áudios individualmente se necessário.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  {Object.values(resolvedAudioMappings).some(m => m.method === "natural_order") && (
                    <div className="flex items-start gap-2.5 p-3.5 bg-amber-50 text-amber-800 border border-amber-100 rounded-xl mb-4">
                      <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                      <span className="text-xs font-semibold leading-relaxed">
                        Os áudios foram associados pela ordem dos arquivos. Verifique antes de gravar.
                      </span>
                    </div>
                  )}

                  <div className="space-y-4">
                    {validationResult.allQuestions.map((q) => {
                      const mapping = resolvedAudioMappings[q.temporaryImportId];
                      const audioFile = mapping?.file || null;
                      const method = mapping?.method || "none";

                      return (
                        <div key={q.temporaryImportId} className="p-4 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-3">
                          <div className="flex items-center justify-between border-b border-gray-100 pb-2 flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-blue-100 text-blue-700 border-none font-bold text-[10px]">
                                Questão {q.importOrder || q.questionNumber || q.rawIndex}
                              </Badge>
                              <span className="font-bold text-gray-800 text-xs">
                                {q.title || `Questão ${q.importOrder || q.questionNumber || q.rawIndex}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              {method === "filename" && (
                                <Badge className="bg-green-100 text-green-700 border-none text-[8px] font-bold px-2 py-0.5 rounded-lg">
                                  Nome do Arquivo
                                </Badge>
                              )}
                              {method === "natural_order" && (
                                <Badge className="bg-amber-100 text-amber-700 border-none text-[8px] font-bold px-2 py-0.5 rounded-lg animate-pulse">
                                  Ordem Natural (Verificar!)
                                </Badge>
                              )}
                              {method === "manual" && (
                                <Badge className="bg-purple-100 text-purple-700 border-none text-[8px] font-bold px-2 py-0.5 rounded-lg">
                                  Manual
                                </Badge>
                              )}
                              {method === "none" && (
                                <Badge className="bg-red-100 text-red-700 border-none text-[8px] font-bold px-2 py-0.5 rounded-lg">
                                  Sem Áudio
                                </Badge>
                              )}
                            </div>
                          </div>

                          <div className="max-w-md">
                            <AudioUploaderWidget
                              label="Áudio Associado"
                              file={audioFile}
                              existingUrl={q.audioUrl}
                              associatedAudioName={q.associatedAudioName}
                              onChange={(file) => {
                                setManualAudioMappings(prev => ({
                                  ...prev,
                                  [q.temporaryImportId]: file
                                }));
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Batch Option Image Uploader */}
            {selectedCompetence === "Listening" && validationResult?.allQuestions?.some(doesQuestionRequireOptionImages) && (
              <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl animate-in fade-in duration-300">
                <CardHeader className="pb-3 bg-gray-50/50 border-b border-gray-100">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <Image className="w-4 h-4 text-purple-500" />
                    Anexar Imagens das Alternativas (Upload em Lote)
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Selecione ou arraste as imagens das alternativas em lote (.png, .jpg, .jpeg, .webp). Elas serão mapeadas automaticamente para cada questão correspondente com base no nome do arquivo.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="border-2 border-dashed border-purple-100 rounded-xl p-6 bg-purple-50/10 text-center hover:bg-purple-50/20 transition-all relative cursor-pointer">
                    <Image className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-700">{t("importer.clickToSelect")}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{t("importer.dragDropImageFormats")}</p>
                    <input
                      type="file"
                      multiple
                      accept="image/png, image/jpeg, image/jpg, image/webp"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) => {
                        if (e.target.files) {
                          handleBatchImageUpload(Array.from(e.target.files));
                        }
                      }}
                    />
                  </div>

                  {batchImageFiles.length > 0 && (
                    <div className="space-y-1.5 animate-in fade-in">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("importer.imageMapping")} ({batchImageFiles.length}):</span>
                      <div className="max-h-[200px] overflow-y-auto space-y-1.5 pr-1">
                        {batchImageFiles.map((f, idx) => {
                          const previewUrl = URL.createObjectURL(f);
                          return (
                            <div key={idx} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl text-xs border border-gray-100/50 hover:bg-gray-100/10 transition-all">
                              <div className="flex items-center gap-2 min-w-0">
                                <img
                                  src={previewUrl}
                                  alt={f.name}
                                  className="w-8 h-8 rounded object-cover border border-gray-200 shrink-0"
                                />
                                <div className="min-w-0">
                                  <span className="font-semibold text-gray-700 truncate max-w-[200px] block">{f.name}</span>
                                  <span className="text-gray-400 text-[10px]">({(f.size / 1024).toFixed(1)} KB)</span>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setBatchImageFiles(prev => prev.filter((_, i) => i !== idx));
                                  setPendingMedia(prev => {
                                    const updated = { ...prev };
                                    const match = matchImageFileToQuestionOption(f, validationResult.allQuestions);
                                    if (match) {
                                      const { temporaryImportId, optionLabel } = match;
                                      if (updated[temporaryImportId]?.optionImages?.[optionLabel] === f) {
                                        updated[temporaryImportId].optionImages = {
                                          ...updated[temporaryImportId].optionImages,
                                          [optionLabel]: null
                                        };
                                      }
                                    }
                                    return updated;
                                  });
                                }}
                                className="p-1 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Ctrl+V Paste Area for Images */}
                  {validationResult?.allQuestions && validationResult.allQuestions.filter(doesQuestionRequireOptionImages).length > 0 && (
                    <div className="pt-4 border-t border-gray-100 space-y-3 animate-in fade-in">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1.5">
                          <Label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                            Questão Destino
                          </Label>
                          <Select
                            value={pastedImageTargetQId}
                            onValueChange={setPastedImageTargetQId}
                          >
                            <SelectTrigger className="w-full h-9 bg-gray-50/50 hover:bg-gray-50 border-gray-200 text-xs font-semibold rounded-xl focus:ring-purple-500/20">
                              <SelectValue placeholder="Selecione a questão alvo" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border-gray-200">
                              {validationResult.allQuestions.filter(doesQuestionRequireOptionImages).map((q) => (
                                <SelectItem
                                  key={q.temporaryImportId}
                                  value={q.temporaryImportId}
                                  className="text-xs font-medium rounded-lg"
                                >
                                  Questão {q.importOrder || q.questionNumber || q.rawIndex}: {q.title || "Sem título"}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <Label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                            Alternativa Alvo
                          </Label>
                          <div className="grid grid-cols-4 gap-1.5 h-9">
                            {(["A", "B", "C", "D"] as const).map((letter) => {
                              const isSelected = pastedImageTargetOption === letter;
                              return (
                                <button
                                  key={letter}
                                  type="button"
                                  onClick={() => setPastedImageTargetOption(letter)}
                                  className={`h-full text-xs font-black rounded-xl border transition-all ${
                                    isSelected
                                      ? "bg-purple-600 border-purple-600 text-white shadow-sm"
                                      : "bg-gray-50/50 hover:bg-gray-50 border-gray-200 text-gray-600"
                                  }`}
                                >
                                  {letter}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      <ClipboardMediaPasteBox
                        mediaType="image"
                        label={`Colar Imagem da Alternativa ${pastedImageTargetOption} via Ctrl+V`}
                        description={`Copie um arquivo de imagem (Ctrl+C) ou uma URL de imagem (.png, .jpg, .webp) para a alternativa ${pastedImageTargetOption}`}
                        acceptedMimeTypes={["image/png", "image/jpeg", "image/jpg", "image/webp"]}
                        acceptedExtensions={[".png", ".jpg", ".jpeg", ".webp"]}
                        onFilePaste={(file) => handleImageFilePaste(file, pastedImageTargetQId, pastedImageTargetOption)}
                        onUrlPaste={(url) => handleImageUrlPaste(url, pastedImageTargetQId, pastedImageTargetOption)}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Mapeamento Individual das Alternativas */}
            {selectedCompetence === "Listening" && validationResult?.allQuestions?.some(doesQuestionRequireOptionImages) && (
              <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl animate-in fade-in duration-300">
                <CardHeader className="pb-3 bg-gray-50/50 border-b border-gray-100">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <Image className="w-4 h-4 text-purple-500" />
                    Mapeamento das Imagens das Alternativas
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Faça o upload ou mapeamento das imagens A, B, C e D para cada questão correspondente.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {validationResult.allQuestions.filter(doesQuestionRequireOptionImages).map((q) => (
                    <div key={q.temporaryImportId} className="p-4 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-4">
                      <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-100 text-purple-700 border-none font-bold text-[10px]">
                            Questão {q.importOrder || q.questionNumber || q.rawIndex}
                          </Badge>
                          <span className="font-bold text-gray-800 text-xs">
                            {q.title || `Conversation ${q.importOrder || q.questionNumber || q.rawIndex}`}
                          </span>
                        </div>
                        <span className="text-[10px] text-gray-400 font-medium">CO / Dialogues associés à des images</span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {(["A", "B", "C", "D"] as const).map(label => {
                          const safeOptions = Array.isArray(q.options) ? q.options : normalizeImportedQuestionsPayload(q.options) || normalizeQuestionOptions(q);
                          const optObj = Array.isArray(safeOptions) ? safeOptions.find((o: any) => o.label === label) : null;
                          return (
                            <div key={label} className="p-3 bg-white rounded-xl border border-gray-100 flex flex-col gap-2">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wide block">
                                Alternativa {label} {q.correctAnswer === label && <span className="text-green-600 font-bold">(Gabarito)</span>}
                              </span>
                              <ImageUploaderWidget
                                label={`Imagem Alternativa ${label}`}
                                file={pendingMedia[q.temporaryImportId]?.optionImages?.[label] || null}
                                existingUrl={isPermanentMediaUrl(optObj?.imageUrl || optObj?.mediaId || optObj?.image) ? (optObj.imageUrl || optObj.mediaId || optObj.image) : undefined}
                                onChange={(file) => {
                                  setPendingMedia(prev => {
                                    const mediaForQ = prev[q.temporaryImportId] || {
                                      supportImageFile: null,
                                      audioFile: null,
                                      optionImages: { A: null, B: null, C: null, D: null }
                                    };
                                    return {
                                      ...prev,
                                      [q.temporaryImportId]: {
                                        ...mediaForQ,
                                        optionImages: {
                                          ...mediaForQ.optionImages,
                                          [label]: file
                                        }
                                      }
                                    };
                                  });
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Save trigger Card */}
            <Card className="border-none shadow-sm bg-white overflow-hidden rounded-2xl">
              <CardHeader className="pb-3 bg-gray-50/50 border-b border-gray-100">
                <CardTitle className="text-sm font-bold text-slate-800">{t("importer.completeImportTitle")}</CardTitle>
                <CardDescription className="text-xs">{t("importer.confirmIntegrityDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start gap-3 p-4 bg-purple-50/40 rounded-xl border border-purple-100/50 text-xs">
                  <AlertCircle className="w-4 h-4 text-purple-600 shrink-0 mt-0.5 animate-pulse" />
                  <div className="space-y-1">
                    <span className="font-bold text-purple-900 block">{t("importer.integrityConfirmationTitle")}</span>
                    <p className="text-slate-600 leading-relaxed font-semibold">
                      Ao prosseguir, as questões validadas com sucesso e suas respectivas mídias físicas serão sincronizadas na base local. O progresso será direcionado ao banco de questões principal.
                    </p>
                  </div>
                </div>

                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setValidationResult(null);
                      setCurrentStep(3);
                    }}
                    className="w-full sm:w-auto rounded-xl h-12 border-gray-200 text-gray-700 font-bold text-xs bg-white hover:bg-slate-50 flex items-center justify-center gap-1.5 shadow-sm"
                  >
                    <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
                    <span>{t("importer.changeContentBtn")}</span>
                  </Button>
                  
                  <Button 
                    className="w-full sm:flex-1 rounded-xl min-h-[3rem] h-auto py-3 bg-purple-600 hover:bg-purple-700 text-white shadow-md font-bold text-xs flex items-center justify-center gap-2 whitespace-normal break-words text-center"
                    disabled={isImporting || !validationResult || allAreDuplicates || (validationResult.validCount === 0 && allowPartialImport) || (!allowPartialImport && validationResult.invalidCount > 0)}
                    onClick={handleImport}
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white shrink-0" />
                        <span>{t("importer.savingQuestionsToSystem")}</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-purple-200 shrink-0" />
                        <span>Gravar Questões ({validationResult?.validCount || 0} {validationResult?.validCount === 1 ? "item" : "itens"})</span>
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Button 
              variant="ghost" 
              size="sm"
              className="w-full h-10 rounded-xl bg-gray-100/40 hover:bg-gray-100 text-gray-500 font-semibold text-xs transition-all"
              onClick={handleClear}
              disabled={isImporting}
            >
              Resetar Importador e Voltar
            </Button>
          </div>

          {/* Sidebar validation panel */}
          <div className="space-y-6 lg:col-span-1">
            <Card className="border-none shadow-sm bg-white h-full rounded-2xl flex flex-col justify-between">
              <CardHeader className="pb-3 border-b border-gray-50 bg-gray-50/50">
                <CardTitle className="text-base font-bold text-gray-800">Painel de Qualidade</CardTitle>
                <CardDescription className="text-xs">Checagem de consistência em tempo real</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4 flex-1">
                {validationResult && (
                  <div className="space-y-4 animate-in fade-in duration-300">
                    
                    {/* General Errors block */}
                    {validationResult.generalErrors && (
                      <div className="p-4 bg-red-50 text-red-800 border border-red-100 rounded-xl space-y-1.5">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                          <div className="text-xs font-semibold leading-relaxed">
                            <span className="font-bold block text-red-900 leading-normal">Importação Rejeitada:</span>
                            {validationResult.generalErrors.map((err, i) => (
                              <p key={i}>• {err}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Summary card alert alerts */}
                    {!validationResult.generalErrors && (
                      <>
                        {allAreDuplicates ? (
                          <div className="flex items-start gap-2.5 p-3.5 bg-amber-50 text-amber-800 border border-amber-100 rounded-2xl">
                            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5 animate-bounce" />
                            <div className="text-xs font-semibold leading-relaxed">
                              <span className="font-bold block text-amber-900 text-sm">Lote Duplicado</span>
                              <span className="mt-0.5 block text-amber-950 font-bold">
                                Nenhuma questão nova para importar. Todas as questões deste lote já existem no banco.
                              </span>
                            </div>
                          </div>
                        ) : validationResult.invalidCount === 0 ? (
                          <div className="flex items-start gap-2.5 p-3.5 bg-green-50 text-green-800 border border-green-100 rounded-2xl">
                            <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                            <div className="text-xs font-semibold leading-relaxed">
                              <span className="font-bold block text-green-900 text-sm">Estrutura Correta!</span>
                              <span className="mt-0.5 block">
                                {validationResult.duplicateCount && validationResult.duplicateCount > 0 ? (
                                  `Todas as ${validationResult.validCount} questões novas estão íntegras e prontas. ${validationResult.duplicateCount} duplicadas serão ignoradas.`
                                ) : (
                                  `Todas as ${validationResult.validCount} questões estão íntegras e prontas.`
                                )}
                              </span>
                            </div>
                          </div>
                        ) : allowPartialImport ? (
                          <div className="flex items-start gap-2.5 p-3.5 bg-amber-50 text-amber-800 border border-amber-100 rounded-2xl">
                            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                            <div className="text-xs font-semibold leading-relaxed">
                              <span className="font-bold block text-amber-900 text-sm">Validação parcial</span>
                              <span className="mt-0.5 block">
                                {validationResult.duplicateCount && validationResult.duplicateCount > 0 ? (
                                  `Apenas as ${validationResult.validCount} questões válidas e novas serão gravadas. ${validationResult.invalidCount} com erro e ${validationResult.duplicateCount} duplicadas serão descartadas/ignoradas.`
                                ) : (
                                  `Apenas as ${validationResult.validCount} questões válidas serão gravadas. ${validationResult.invalidCount} serão descartadas.`
                                )}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2.5 p-3.5 bg-red-50 text-red-800 border border-red-100 rounded-2xl">
                            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                            <div className="text-xs font-semibold leading-relaxed">
                              <span className="font-bold block text-red-900 text-sm">Bloco Recusado</span>
                              <span className="mt-0.5 block">
                                O pacote possui {validationResult.invalidCount} questões com erros. Ative a "Importação parcial" ou altere o conteúdo para prosseguir.
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {selectedCompetence === "Writing" && !validationResult.generalErrors && (
                      <div className="flex items-start gap-2 p-3 bg-blue-50 text-blue-800 border border-blue-100 rounded-xl text-[11px] font-semibold leading-relaxed">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                        <div>
                          <span className="font-bold text-blue-950 block">Nota sobre EE:</span>
                          Questão de Expression Écrite: gabarito objetivo e explicação não são exigidos.
                        </div>
                      </div>
                    )}

                    {/* Audio Mismatch Warnings */}
                    {activeAudioWarning && (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 text-amber-800 border border-amber-100 rounded-xl text-[11px] font-semibold leading-relaxed">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                          <span className="font-bold text-amber-950 block">Aviso de Mídia:</span>
                          {activeAudioWarning}
                        </div>
                      </div>
                    )}

                    {/* Option Image Mismatch Warnings */}
                    {activeImageWarning && (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 text-amber-800 border border-amber-100 rounded-xl text-[11px] font-semibold leading-relaxed">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                          <span className="font-bold text-amber-950 block">Aviso de Mídia (Imagens):</span>
                          {activeImageWarning}
                        </div>
                      </div>
                    )}

                    {/* Quantitative statistics */}
                    {!validationResult.generalErrors && (
                      <div className="grid grid-cols-3 gap-2 text-center pt-1">
                        <div className="bg-green-50/40 p-3 rounded-2xl border border-green-100">
                          <span className="text-[10px] text-green-600 block font-bold uppercase tracking-wide">Válidas</span>
                          <span className="text-lg font-black text-green-700">{validationResult.validCount}</span>
                        </div>
                        <div className="bg-amber-50/40 p-3 rounded-2xl border border-amber-100">
                          <span className="text-[10px] text-amber-600 block font-bold uppercase tracking-wide">Duplicadas</span>
                          <span className="text-lg font-black text-amber-700">{validationResult.duplicateCount || 0}</span>
                        </div>
                        <div className="bg-red-50/40 p-3 rounded-2xl border border-red-100">
                          <span className="text-[10px] text-red-600 block font-bold uppercase tracking-wide">Erros</span>
                          <span className="text-lg font-black text-red-700">{validationResult.invalidCount}</span>
                        </div>
                      </div>
                    )}

                    {/* Collapsible individual preview cards list */}
                    {!validationResult.generalErrors && (
                      <div className="space-y-2 pt-2">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider block">Lista de questões ({validationResult.allQuestions.length})</span>
                        <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                          {validationResult.allQuestions.map((q, idx) => {
                            const isExpanded = expandedIndex === idx;
                            return (
                              <div
                                key={idx}
                                className={`p-3 rounded-xl border transition-all ${
                                  q.isDuplicate
                                    ? "bg-amber-50/10 border-amber-100/70 hover:bg-amber-50/20"
                                    : q.isValid 
                                      ? "bg-green-50/10 border-green-100/70 hover:bg-green-50/20" 
                                      : "bg-red-50/10 border-red-100/70 hover:bg-red-50/20"
                                }`}
                              >
                                <div
                                  className="flex items-center justify-between cursor-pointer"
                                  onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                                >
                                  <div className="space-y-1 min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <Badge className="bg-primary/5 text-primary border-none text-[8px] font-bold px-1.5 py-0">
                                        Questão {q.rawIndex}
                                      </Badge>
                                      <span className="text-[9px] text-gray-400 font-bold">{q.difficultyLabel || "Médio"}</span>
                                      {q.isDuplicate ? (
                                        <Badge className="bg-amber-100 text-amber-700 border-none text-[8px] px-1.5 py-0 font-bold">Duplicada</Badge>
                                      ) : q.isValid ? (
                                        <Badge className="bg-green-100 text-green-700 border-none text-[8px] px-1.5 py-0 font-bold">Válida</Badge>
                                      ) : (
                                        <Badge className="bg-red-100 text-red-700 border-none text-[8px] px-1.5 py-0 font-bold">Erro</Badge>
                                      )}
                                    </div>
                                    <h5 className="font-bold text-gray-800 text-xs truncate max-w-[150px]">{q.title}</h5>
                                  </div>
                                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                                </div>

                                {isExpanded && (
                                  <div className="mt-3 pt-3 border-t border-gray-100/50 text-[11px] space-y-3 text-gray-600 animate-in fade-in duration-200">
                                    
                                    {/* Error list display for current expanded card */}
                                    {q.errors.length > 0 && (
                                      <div className="p-3 bg-red-50 text-red-800 border border-red-100 rounded-xl space-y-1">
                                        <span className="text-[9px] font-bold text-red-950 uppercase tracking-wider block">Erros Críticos ({q.errors.length}):</span>
                                        {q.errors.map((err: string, i: number) => (
                                          <p key={i} className="text-[10px] font-semibold pl-1.5 leading-relaxed">• {err}</p>
                                        ))}
                                      </div>
                                    )}

                                    {/* Warnings list */}
                                    {q.warnings.length > 0 && (
                                      <div className="p-3 bg-amber-50 text-amber-800 border border-amber-100 rounded-xl space-y-1">
                                        <span className="text-[9px] font-bold text-amber-950 uppercase tracking-wider block">Avisos e Notas ({q.warnings.length}):</span>
                                        {q.warnings.map((warn: string, i: number) => (
                                          <p key={i} className="text-[10px] font-semibold pl-1.5 leading-relaxed">• {warn}</p>
                                        ))}
                                      </div>
                                    )}

                                    <div>
                                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide block">Seção do Exame:</span>
                                      <span className="text-gray-800 font-bold text-xs">{q.section}</span>
                                    </div>

                                    {q.subSection && (
                                      <div>
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide block">Subtipo Interno:</span>
                                        <Badge className="bg-purple-100 text-purple-900 border-none font-bold text-[10px] mt-0.5">
                                          {q.subSection === "phrases_lacunaires" ? "Phrases lacunaires" :
                                           q.subSection === "textes_lacunaires" ? "Textes lacunaires" :
                                           q.subSection === "lecture_rapide_textes" ? "Lecture rapide de textes" :
                                           q.subSection === "lecture_rapide_graphiques" ? "Lecture rapide de graphiques" : q.subSection}
                                        </Badge>
                                      </div>
                                    )}

                                    {q.instruction && (
                                      <div>
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide block">Enunciado / Instrução:</span>
                                        <p className="text-gray-700 italic font-medium leading-relaxed">{q.instruction}</p>
                                      </div>
                                    )}

                                    {q.text && (
                                      <div>
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide block">Texto de Suporte (Contexto):</span>
                                        <p className="p-2.5 bg-gray-50 rounded-xl text-gray-700 font-medium whitespace-pre-wrap max-h-[100px] overflow-y-auto leading-relaxed border border-gray-100/50">
                                          {blindMode && q.text.length > 100 ? `${q.text.substring(0, 100)}...` : q.text}
                                        </p>
                                      </div>
                                    )}

                                    {/* Physical support image preview & uploader widget */}
                                    {selectedCompetence === "Reading" && q.section === "Lecture rapide de textes et de graphiques" && (
                                      <div className="space-y-1.5 border border-purple-100/60 p-3 rounded-2xl bg-purple-50/10">
                                        <ImageUploaderWidget
                                          label="Imagem / gráfico da questão"
                                          file={pendingMedia[q.temporaryImportId]?.supportImageFile || null}
                                          existingUrl={isPermanentMediaUrl(q.imageUrl) ? q.imageUrl : undefined}
                                          onChange={(file) => {
                                            setPendingMedia(prev => {
                                              const mediaForQ = prev[q.temporaryImportId] || { supportImageFile: null, audioFile: null, optionImages: { A: null, B: null, C: null, D: null } };
                                              return {
                                                ...prev,
                                                [q.temporaryImportId]: {
                                                  ...mediaForQ,
                                                  supportImageFile: file
                                                }
                                              };
                                            });
                                          }}
                                        />
                                      </div>
                                    )}

                                    {/* Physical audio preview & uploader widget */}
                                    {selectedCompetence === "Listening" && (
                                      <div className="space-y-1.5 border border-blue-100/60 p-3 rounded-2xl bg-blue-50/10">
                                        <AudioUploaderWidget
                                          label="Arquivo de áudio da questão"
                                          file={pendingMedia[q.temporaryImportId]?.audioFile || resolvedAudioMappings[q.temporaryImportId]?.file || null}
                                          existingUrl={isPermanentMediaUrl(q.audioUrl) ? q.audioUrl : undefined}
                                          associatedAudioName={q.associatedAudioName}
                                          onChange={(file) => {
                                            setPendingMedia(prev => {
                                              const mediaForQ = prev[q.temporaryImportId] || { supportImageFile: null, audioFile: null, optionImages: { A: null, B: null, C: null, D: null } };
                                              return {
                                                ...prev,
                                                [q.temporaryImportId]: {
                                                  ...mediaForQ,
                                                  audioFile: file
                                                }
                                              };
                                            });
                                          }}
                                        />
                                      </div>
                                    )}

                                    {/* MCQ Options list preview */}
                                    {q.type === "mcq" && q.section !== "Dialogues associés à des images" && q.alternatives && (
                                      <div className="space-y-1.5">
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">Alternativas de múltipla escolha:</span>
                                        {Object.entries(q.alternatives).map(([key, val]) => {
                                          const highlight = !blindMode && key === q.correctAnswer;
                                          return (
                                            <div 
                                              key={key} 
                                              className={`p-2 rounded-xl border flex items-center gap-2 transition-all ${
                                                highlight 
                                                  ? "bg-green-50 border-green-200 text-green-800 font-bold" 
                                                  : "bg-white border-gray-100 text-gray-600"
                                              }`}
                                            >
                                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black border ${
                                                highlight 
                                                  ? "bg-green-500 text-white border-green-500" 
                                                  : "bg-gray-50 text-gray-400 border-gray-100"
                                              }`}>{key}</span>
                                              <span className="truncate flex-1">{val as string}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* MCQ Dialogues associés à des images options list preview & uploader widget */}
                                    {q.type === "mcq" && q.section === "Dialogues associés à des images" && (
                                      <div className="space-y-2 border border-blue-100/60 p-3 rounded-2xl bg-blue-50/10">
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">Alternativas de Imagem:</span>
                                        <div className="grid grid-cols-2 gap-3">
                                          {(["A", "B", "C", "D"] as const).map(label => {
                                            const safeOptions = Array.isArray(q.options) ? q.options : normalizeImportedQuestionsPayload(q.options) || normalizeQuestionOptions(q);
                                            const optObj = Array.isArray(safeOptions) ? safeOptions.find((o: any) => o.label === label) : null;
                                            const highlight = !blindMode && label === q.correctAnswer;
                                            return (
                                              <div key={label} className={`p-2 rounded-xl border flex flex-col gap-2 relative transition-all ${
                                                highlight ? "bg-green-50/40 border-green-200" : "bg-white border-gray-100"
                                              }`}>
                                                <div className="flex items-center gap-1.5">
                                                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black border ${
                                                    highlight 
                                                      ? "bg-green-500 text-white border-green-500" 
                                                      : "bg-gray-50 text-gray-400 border-gray-100"
                                                  }`}>{label}</span>
                                                  {highlight && <Badge className="bg-green-500 text-white text-[8px] font-bold px-1.5 py-0 border-none">Gabarito</Badge>}
                                                </div>
                                                <ImageUploaderWidget
                                                  label={`Imagem Alternativa ${label}`}
                                                  file={pendingMedia[q.temporaryImportId]?.optionImages?.[label] || null}
                                                  existingUrl={isPermanentMediaUrl(optObj?.imageUrl || optObj?.mediaId || optObj?.image) ? (optObj.imageUrl || optObj.mediaId || optObj.image) : undefined}
                                                  onChange={(file) => {
                                                    setPendingMedia(prev => {
                                                      const mediaForQ = prev[q.temporaryImportId] || { supportImageFile: null, audioFile: null, optionImages: { A: null, B: null, C: null, D: null } };
                                                      return {
                                                        ...prev,
                                                        [q.temporaryImportId]: {
                                                          ...mediaForQ,
                                                          optionImages: {
                                                            ...mediaForQ.optionImages,
                                                            [label]: file
                                                          }
                                                        }
                                                      };
                                                    });
                                                  }}
                                                />
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {/* Correct answer block */}
                                    {!blindMode && q.type === "mcq" && q.correctAnswer && (
                                      <div className="p-2.5 bg-green-50/40 border border-green-100 rounded-xl">
                                        <span className="text-[9px] font-bold text-green-700 uppercase tracking-wider block">Gabarito Oficial:</span>
                                        <span className="text-green-800 font-black text-xs">{q.correctAnswer}</span>
                                      </div>
                                    )}

                                    {!blindMode && q.explanation && (
                                      <div className="p-2.5 bg-gray-50 border border-gray-100 rounded-xl">
                                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">Explicação da Questão:</span>
                                        <p className="text-gray-600 font-medium leading-relaxed mt-0.5">{q.explanation}</p>
                                      </div>
                                    )}

                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </CardContent>
            </Card>
          </div>

        </div>
      )}

    </div>
  );
}
